import React, { useState, useEffect, useDeferredValue } from 'react';
import DebouncedSearchInput from '../../components/ui/debounced-search-input';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import { EngancheTab } from '../../components/admin/EngancheTab';
import AIEventsTab from '../../components/admin/AIEventsTab';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from '../../components/ui/select';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { formatDateTimeChile, formatChile } from '../../utils/dateUtils';
import {
  Users, Calendar, Store, CheckCircle, XCircle, Eye,
  TrendingUp, Clock, Star, BarChart3, Plus, Edit2, Trash2,
  MapPin, Tag, Loader2, Globe, UserPlus, Link2, Settings, Upload, Image, Bell, Mail, Phone, Download, FileSpreadsheet, AlertTriangle, CreditCard, Crown, Zap, Camera, Moon, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Copy, ExternalLink, GalleryHorizontal, Search, Landmark, Instagram
} from 'lucide-react';
import { toast } from 'sonner';
import { useSiteLogo } from '../../contexts/SiteLogoContext';
import { Footer } from '../../components/Footer';
import ScrapingTab from '../../components/admin/ScrapingTab';
import ImageBackfillCard from '../../components/admin/ImageBackfillCard';
import PriceFixCard from '../../components/admin/PriceFixCard';
import SeoIntroRegenCard from '../../components/admin/SeoIntroRegenCard';
import VenueBackfillCard from '../../components/admin/VenueBackfillCard';
import VenuesTab from '../../components/admin/VenuesTab';
import OrganizersTab from '../../components/admin/OrganizersTab';
import ViewsBoostTab from '../../components/admin/ViewsBoostTab';
import DuplicatesTab from '../../components/admin/DuplicatesTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminDashboard = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const { logoUrl, darkLogoUrl, refreshLogo, getFullLogoUrl, getFullDarkLogoUrl } = useSiteLogo();

  const [stats, setStats] = useState(null);
  const [pendingEvents, setPendingEvents] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allProviders, setAllProviders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [regions, setRegions] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [submitting, setSubmitting] = useState(false);

  // Dialog states
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [regionDialogOpen, setRegionDialogOpen] = useState(false);
  const [communeDialogOpen, setCommuneDialogOpen] = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [providerDialogOpen, setProviderDialogOpen] = useState(false);
  const [assignUserDialogOpen, setAssignUserDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingRegion, setEditingRegion] = useState(null);
  const [editingCommune, setEditingCommune] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ type: '', id: '', name: '' });
  const [deleting, setDeleting] = useState(false);

  // Generic confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    description: '',
    onConfirm: null,
    variant: 'default' // 'default' or 'destructive'
  });

  const showConfirmDialog = (title, description, onConfirm, variant = 'default') => {
    setConfirmDialog({
      open: true,
      title,
      description,
      onConfirm,
      variant
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog(prev => ({ ...prev, open: false }));
  };

  // Form states
  const [newCategory, setNewCategory] = useState({ name: '', name_en: '', name_pt: '', icon: '', color: '', image_url: '' });
  const [newRegion, setNewRegion] = useState({ name: '', country_id: '' });
  const [newCommune, setNewCommune] = useState({ name: '', region_id: '' });
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', role: 'provider' });
  const [newProvider, setNewProvider] = useState({ business_name: '', description: '', phone: '', website: '', commune_id: '' });
  const [selectedUserId, setSelectedUserId] = useState('');

  // Site logo state
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingDarkLogo, setUploadingDarkLogo] = useState(false);
  const logoInputRef = React.useRef(null);
  const darkLogoInputRef = React.useRef(null);

  // Favicon state
  const [faviconUrl, setFaviconUrl] = useState('');
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const faviconInputRef = React.useRef(null);

  // Visit analytics state
  const [visitStats, setVisitStats] = useState(null);

  // Platform contact messages state
  const [platformMessages, setPlatformMessages] = useState([]);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // Provider unseen counter (NUEVO badge in sidebar)
  const [unseenProvidersCount, setUnseenProvidersCount] = useState(0);

  // Event engagement stats (admin Estadísticas tab)
  const [eventStats, setEventStats] = useState([]);
  const [eventStatsSort, setEventStatsSort] = useState('views');
  const [loadingEventStats, setLoadingEventStats] = useState(false);
  const [eventStatsPage, setEventStatsPage] = useState(1);
  const EVENT_STATS_PER_PAGE = 10;

  // Event-stats toolbar filters
  const [esSearch, setEsSearch] = useState('');
  const deferredEsSearch = useDeferredValue(esSearch);
  const [esDate, setEsDate] = useState(''); // YYYY-MM-DD
  const [esType, setEsType] = useState('all'); // all | regular | recurring | permanent | online
  const [esStatus, setEsStatus] = useState('all'); // all | active | pending | expired | rejected | inactive
  const [esProvider, setEsProvider] = useState('all'); // provider_id or 'all'
  const [esRegion, setEsRegion] = useState('all'); // region_id or 'all'
  const [esCommune, setEsCommune] = useState('all'); // commune_id or 'all'
  // Views chart modal (opened when a row is clicked)
  const [chartEvent, setChartEvent] = useState(null);
  const [chartDays, setChartDays] = useState(30);
  const [chartData, setChartData] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [hoverIdx, setHoverIdx] = useState(null);

  // Site info state
  const [siteInfo, setSiteInfo] = useState({
    platform_name: 'PlanCity',
    version: '1.0.0'
  });
  const [editingSiteInfo, setEditingSiteInfo] = useState(false);
  const [savingSiteInfo, setSavingSiteInfo] = useState(false);

  // Hero images state
  const [heroImages, setHeroImages] = useState([]);
  const [uploadingHeroIndex, setUploadingHeroIndex] = useState(null);
  const heroInputRefs = [React.useRef(null), React.useRef(null), React.useRef(null), React.useRef(null)];

  // Social proof state (trust bar: avatars + count)
  const [socialProof, setSocialProof] = useState({ count: 2500, photos: ['', '', '', ''] });
  const [socialProofCountDraft, setSocialProofCountDraft] = useState('2500');
  const [savingSocialProofCount, setSavingSocialProofCount] = useState(false);
  const [uploadingSocialIndex, setUploadingSocialIndex] = useState(null);
  const socialProofInputRefs = [React.useRef(null), React.useRef(null), React.useRef(null), React.useRef(null)];

  // Subscriptions state
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionStats, setSubscriptionStats] = useState(null);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);

  // Plans state
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [newPlan, setNewPlan] = useState({
    plan_key: '',
    name: '',
    description: '',
    price_monthly: 0,
    price_yearly: 0,
    max_events: 5,
    max_featured_events: 0,
    is_active: true,
    display_order: 0,
    features: []
  });
  const [newFeature, setNewFeature] = useState({ name: '', is_included: true });
  const [adminBillingPreview, setAdminBillingPreview] = useState('monthly');
  const [editingFeature, setEditingFeature] = useState(null);

  // Territory import state
  const [importingTerritories, setImportingTerritories] = useState(false);
  const territoryFileInputRef = React.useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Use allSettled so a single transient failure (e.g. HTTP/2 stream reset,
      // ERR_ABORTED under high concurrency) doesn't collapse the entire dashboard.
      const results = await Promise.allSettled([
        axios.get(`${API_URL}/api/admin/stats`),
        axios.get(`${API_URL}/api/admin/events/pending`),
        axios.get(`${API_URL}/api/admin/users`),
        axios.get(`${API_URL}/api/admin/providers`),
        axios.get(`${API_URL}/api/admin/categories`),
        axios.get(`${API_URL}/api/admin/regions`),
        axios.get(`${API_URL}/api/admin/communes`),
        axios.get(`${API_URL}/api/territories/countries`),
        axios.get(`${API_URL}/api/analytics/visits`),
        axios.get(`${API_URL}/api/admin/platform-messages/unread-count`),
        axios.get(`${API_URL}/api/admin/providers/unseen-count`),
      ]);
      const val = (idx, fallback = null) => (results[idx].status === 'fulfilled' ? results[idx].value.data : fallback);
      if (val(0)) setStats(val(0));
      if (val(1)) setPendingEvents(val(1));
      if (val(2)) setAllUsers(val(2));
      if (val(3)) setAllProviders(val(3));
      if (val(4)) setCategories(val(4));
      if (val(5)) setRegions(val(5));
      if (val(6)) setCommunes(val(6));
      if (val(7)) setCountries(val(7));
      setVisitStats(val(8));
      setUnreadMessagesCount(val(9, { unread: 0 })?.unread || 0);
      setUnseenProvidersCount(val(10, { unseen: 0 })?.unseen || 0);
      // Log any failures for observability without breaking the UI.
      results.forEach((r, i) => {
        if (r.status === 'rejected') console.warn(`admin fetch #${i} failed:`, r.reason?.message || r.reason);
      });
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      setLoadingSubscriptions(true);
      const token = localStorage.getItem('token');
      const [subsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/subscriptions/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/subscriptions/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      setSubscriptions(subsRes.data);
      setSubscriptionStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch subscriptions:', error);
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  const handleDeleteSubscription = async (subId) => {
    showConfirmDialog(
      'Eliminar suscripción',
      '¿Estás seguro de eliminar esta suscripción?',
      async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`${API_URL}/api/subscriptions/${subId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          toast.success('Suscripción eliminada');
          fetchSubscriptions();
        } catch (error) {
          console.error('Failed to delete subscription:', error);
          toast.error('Error al eliminar suscripción');
        }
        closeConfirmDialog();
      },
      'destructive'
    );
  };

  const handleToggleSubscription = async (subId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/api/subscriptions/${subId}/toggle`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchSubscriptions();
    } catch (error) {
      console.error('Failed to toggle subscription:', error);
      toast.error('Error al cambiar estado');
    }
  };

  // ==================== PLATFORM MESSAGES (Contact form) ====================
  const fetchPlatformMessages = async () => {
    try {
      setLoadingMessages(true);
      const [listRes, unreadRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/platform-messages`),
        axios.get(`${API_URL}/api/admin/platform-messages/unread-count`)
      ]);
      setPlatformMessages(listRes.data || []);
      setUnreadMessagesCount(unreadRes.data?.unread || 0);
    } catch (error) {
      console.error('Failed to fetch platform messages:', error);
      toast.error('Error al cargar mensajes');
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleToggleMessageRead = async (msgId) => {
    try {
      const { data } = await axios.put(`${API_URL}/api/admin/platform-messages/${msgId}/toggle-read`);
      setPlatformMessages((prev) => prev.map((m) => (m.id === msgId ? data : m)));
      // Update unread badge optimistically
      const unread = await axios.get(`${API_URL}/api/admin/platform-messages/unread-count`);
      setUnreadMessagesCount(unread.data?.unread || 0);
    } catch (error) {
      console.error('Failed to toggle message:', error);
      toast.error('Error al actualizar mensaje');
    }
  };

  const handleDeletePlatformMessage = (msgId) => {
    showConfirmDialog(
      'Eliminar mensaje',
      '¿Eliminar este mensaje? Esta acción no se puede deshacer.',
      async () => {
        try {
          await axios.delete(`${API_URL}/api/admin/platform-messages/${msgId}`);
          setPlatformMessages((prev) => prev.filter((m) => m.id !== msgId));
          const unread = await axios.get(`${API_URL}/api/admin/platform-messages/unread-count`);
          setUnreadMessagesCount(unread.data?.unread || 0);
          toast.success('Mensaje eliminado');
        } catch (error) {
          console.error('Failed to delete message:', error);
          toast.error('Error al eliminar mensaje');
        }
        closeConfirmDialog();
      },
      'destructive'
    );
  };

  const subjectLabel = (key) => {
    const map = {
      publish: 'Quiero publicar mis eventos',
      publish_others: 'Quiero publicar eventos de otros',
      ambassador: 'Embajador PlanCity',
      support: 'Necesito soporte técnico',
      other: 'Otro',
      // Legacy values from earlier submissions
      general: 'Consulta general',
      technical: 'Problema técnico',
      provider_support: 'Soporte proveedor',
    };
    return map[key] || key;
  };

  // ==================== PROVIDER NUEVO BADGE ====================
  const handleMarkProviderSeen = async (providerId) => {
    try {
      await axios.put(`${API_URL}/api/admin/providers/${providerId}/mark-seen`);
      setAllProviders(prev => prev.map(p => (p.id === providerId ? { ...p, seen_by_admin: true } : p)));
      setUnseenProvidersCount(c => Math.max(0, c - 1));
    } catch (error) {
      console.error('Failed to mark provider as seen:', error);
      toast.error('Error al marcar proveedor como visto');
    }
  };

  // ==================== EVENT ENGAGEMENT STATS ====================
  const fetchEventStats = async (sort = eventStatsSort) => {
    try {
      setLoadingEventStats(true);
      const { data } = await axios.get(`${API_URL}/api/admin/events/all-stats?sort_by=${sort}`);
      setEventStats(data || []);
      setEventStatsPage(1); // reset to page 1 on each fetch / sort change
    } catch (error) {
      console.error('Failed to fetch event stats:', error);
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoadingEventStats(false);
    }
  };

  // Toggle "Imperdible" (carousel featured_type) directly from the stats row.
  // Reuses the shared PUT /events/{id}/feature endpoint that already handles
  // admin callers and enforces the provider's carousel slot count. We update
  // the local list in-place so the UI feels instant without refetching all
  // rows.
  const [imperdibleBusyId, setImperdibleBusyId] = useState(null);
  const toggleImperdible = async (ev) => {
    const isCurrentlyCarousel = ev.featured_type === 'carousel';
    const nextType = isCurrentlyCarousel ? null : 'carousel';
    setImperdibleBusyId(ev.id);
    try {
      await axios.put(`${API_URL}/api/events/${ev.id}/feature`, { featured_type: nextType });
      setEventStats(prev => prev.map(x => x.id === ev.id
        ? { ...x, featured_type: nextType, is_featured: nextType !== null }
        : x));
      toast.success(nextType ? 'Marcado como Imperdible' : 'Quitado de Imperdibles');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo actualizar');
    } finally {
      setImperdibleBusyId(null);
    }
  };

  // Loads the daily views series for the currently-open chartEvent.
  const loadChartSeries = async (eventId, days) => {
    setChartLoading(true);
    setChartData(null);
    setHoverIdx(null);
    try {
      const res = await axios.get(`${API_URL}/api/analytics/events/${eventId}/views-series?days=${days}`);
      setChartData(res.data);
    } catch (e) {
      toast.error('Error al cargar la serie de visualizaciones');
    } finally {
      setChartLoading(false);
    }
  };

  const openChart = (ev) => {
    setChartEvent(ev);
    setChartDays(30);
    loadChartSeries(ev.id, 30);
  };

  // ==================== PLANS MANAGEMENT ====================
  const fetchPlans = async () => {
    try {
      setLoadingPlans(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/plans`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleSavePlan = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      if (editingPlan) {
        await axios.put(`${API_URL}/api/admin/plans/${editingPlan.id}`, newPlan, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success('Plan actualizado correctamente');
      } else {
        await axios.post(`${API_URL}/api/admin/plans`, newPlan, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success('Plan creado correctamente');
      }
      setPlanDialogOpen(false);
      setEditingPlan(null);
      resetPlanForm();
      fetchPlans();
    } catch (error) {
      console.error('Failed to save plan:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar plan');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePlan = async (planId) => {
    showConfirmDialog(
      'Eliminar plan',
      '¿Estás seguro de eliminar este plan? Solo se puede eliminar si no tiene suscriptores activos.',
      async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`${API_URL}/api/admin/plans/${planId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          toast.success('Plan eliminado');
          fetchPlans();
        } catch (error) {
          console.error('Failed to delete plan:', error);
          toast.error(error.response?.data?.detail || 'Error al eliminar plan');
        }
        closeConfirmDialog();
      },
      'destructive'
    );
  };

  const openEditPlanDialog = (plan) => {
    setEditingPlan(plan);
    setNewPlan({
      plan_key: plan.plan_key,
      name: plan.name,
      description: plan.description || '',
      price_monthly: plan.price_monthly,
      price_yearly: plan.price_yearly,
      max_events: plan.max_events,
      max_featured_events: plan.max_featured_events,
      is_active: plan.is_active,
      display_order: plan.display_order,
      features: plan.features || []
    });
    setPlanDialogOpen(true);
  };

  const resetPlanForm = () => {
    setNewPlan({
      plan_key: '',
      name: '',
      description: '',
      price_monthly: 0,
      price_yearly: 0,
      max_events: 5,
      max_featured_events: 0,
      is_active: true,
      display_order: 0,
      features: []
    });
    setNewFeature({ name: '', is_included: true });
    setEditingFeature(null);
  };

  // Feature management functions
  const handleAddFeature = () => {
    if (!newFeature.name.trim()) return;
    const feature = {
      id: `temp-${Date.now()}`,
      name: newFeature.name.trim(),
      is_included: newFeature.is_included,
      display_order: newPlan.features.length
    };
    setNewPlan(prev => ({
      ...prev,
      features: [...prev.features, feature]
    }));
    setNewFeature({ name: '', is_included: true });
  };

  const handleUpdateFeature = (featureId, updates) => {
    setNewPlan(prev => ({
      ...prev,
      features: prev.features.map(f => 
        f.id === featureId ? { ...f, ...updates } : f
      )
    }));
  };

  const handleRemoveFeature = (featureId) => {
    setNewPlan(prev => ({
      ...prev,
      features: prev.features.filter(f => f.id !== featureId)
    }));
  };

  const handleMoveFeature = (index, direction) => {
    const target = index + direction;
    setNewPlan(prev => {
      const features = [...prev.features];
      if (target < 0 || target >= features.length) return prev;
      [features[index], features[target]] = [features[target], features[index]];
      return { ...prev, features: features.map((f, i) => ({ ...f, display_order: i })) };
    });
  };

  const handleSaveFeatureToBackend = async (planId, feature) => {
    try {
      const token = localStorage.getItem('token');
      if (feature.id.startsWith('temp-')) {
        // Create new feature
        await axios.post(`${API_URL}/api/admin/plans/${planId}/features`, {
          name: feature.name,
          is_included: feature.is_included,
          display_order: feature.display_order
        }, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } else {
        // Update existing feature
        await axios.put(`${API_URL}/api/admin/plans/${planId}/features/${feature.id}`, {
          name: feature.name,
          is_included: feature.is_included,
          display_order: feature.display_order
        }, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      toast.success('Característica guardada');
      fetchPlans();
    } catch (error) {
      console.error('Failed to save feature:', error);
      toast.error('Error al guardar característica');
    }
  };

  const handleDeleteFeatureFromBackend = async (planId, featureId) => {
    showConfirmDialog(
      'Eliminar característica',
      '¿Estás seguro de eliminar esta característica?',
      async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`${API_URL}/api/admin/plans/${planId}/features/${featureId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          toast.success('Característica eliminada');
          fetchPlans();
        } catch (error) {
          console.error('Failed to delete feature:', error);
          toast.error('Error al eliminar característica');
        }
        closeConfirmDialog();
      },
      'destructive'
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Format date to Chile timezone - use shared utility
  const formatDateChile = formatDateTimeChile;

  const handleApproveEvent = async (eventId) => {
    try {
      await axios.put(`${API_URL}/api/admin/events/${eventId}/approve`);
      fetchData();
    } catch (error) {
      console.error('Failed to approve event:', error);
    }
  };

  const handleRejectEvent = async (eventId) => {
    showConfirmDialog(
      'Rechazar evento',
      '¿Estás seguro de rechazar este evento?',
      async () => {
        try {
          await axios.put(`${API_URL}/api/admin/events/${eventId}/reject`);
          fetchData();
          toast.success('Evento rechazado');
        } catch (error) {
          console.error('Failed to reject event:', error);
          toast.error('Error al rechazar evento');
        }
        closeConfirmDialog();
      },
      'destructive'
    );
  };

  const handleVerifyProvider = async (providerId) => {
    try {
      await axios.put(`${API_URL}/api/providers/${providerId}/verify`);
      fetchData();
      toast.success('Proveedor verificado');
    } catch (error) {
      console.error('Failed to verify provider:', error);
      toast.error('Error al verificar proveedor');
    }
  };

  const handleToggleActiveOrganizer = async (providerId) => {
    try {
      const response = await axios.put(`${API_URL}/api/providers/${providerId}/toggle-active-organizer`);
      fetchData();
      toast.success(response.data.message);
    } catch (error) {
      console.error('Failed to toggle active organizer:', error);
      toast.error('Error al actualizar medalla');
    }
  };

  const handleTogglePartner = async (providerId) => {
    try {
      const response = await axios.put(`${API_URL}/api/providers/${providerId}/toggle-partner`);
      fetchData();
      toast.success(response.data.message);
    } catch (error) {
      console.error('Failed to toggle partner:', error);
      toast.error('Error al actualizar sello Partner');
    }
  };

  const handleChangeProviderPlan = async (providerId, planKey) => {
    try {
      const response = await axios.put(`${API_URL}/api/admin/providers/${providerId}/change-plan`, {
        plan_key: planKey,
        billing_cycle: 'monthly'
      });
      fetchData();
      toast.success(response.data.message);
    } catch (error) {
      console.error('Failed to change plan:', error);
      toast.error(error.response?.data?.detail || 'Error al cambiar plan');
    }
  };

  const [trialDialogOpen, setTrialDialogOpen] = useState(false);
  const [trialProvider, setTrialProvider] = useState(null);
  const [trialDays, setTrialDays] = useState(30);
  const [trialPlanKey, setTrialPlanKey] = useState('pro');

  const [bonusDialogOpen, setBonusDialogOpen] = useState(false);
  const [bonusProvider, setBonusProvider] = useState(null);
  const [bonusFeatured, setBonusFeatured] = useState(0);
  const [bonusCarousel, setBonusCarousel] = useState(0);

  const openBonusDialog = (provider) => {
    setBonusProvider(provider);
    setBonusFeatured(provider.bonus_featured_slots || 0);
    setBonusCarousel(provider.bonus_carousel_slots || 0);
    setBonusDialogOpen(true);
  };

  const handleSaveBonus = async () => {
    if (!bonusProvider) return;
    try {
      const r = await axios.put(`${API_URL}/api/admin/providers/${bonusProvider.id}/bonus-slots`, {
        bonus_featured_slots: parseInt(bonusFeatured, 10) || 0,
        bonus_carousel_slots: parseInt(bonusCarousel, 10) || 0,
      });
      toast.success(r.data.message);
      setBonusDialogOpen(false);
      setBonusProvider(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar slots extra');
    }
  };

  const openTrialDialog = (provider) => {
    setTrialProvider(provider);
    setTrialDays(provider.trial_days_remaining || 30);
    setTrialPlanKey(provider.trial_plan || 'pro');
    setTrialDialogOpen(true);
  };

  const handleSaveTrial = async () => {
    if (!trialProvider) return;
    try {
      const r = await axios.put(`${API_URL}/api/admin/providers/${trialProvider.id}/trial`, {
        days: parseInt(trialDays, 10) || 0,
        plan_key: trialPlanKey,
      });
      toast.success(r.data.message);
      setTrialDialogOpen(false);
      setTrialProvider(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar trial');
    }
  };

  const handleCancelTrial = async () => {
    if (!trialProvider) return;
    try {
      await axios.put(`${API_URL}/api/admin/providers/${trialProvider.id}/trial`, { days: 0 });
      toast.success('Trial cancelado');
      setTrialDialogOpen(false);
      setTrialProvider(null);
      fetchData();
    } catch (err) {
      toast.error('Error al cancelar trial');
    }
  };

  const handleDeleteProvider = async (provider) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/providers/${provider.id}/cascade-info`);
      const info = res.data;
      const details = [];
      if (info.events_count > 0) details.push(`${info.events_count} evento(s)`);
      if (info.users_count > 0) details.push(`${info.users_count} usuario(s) asignado(s)`);
      const cascadeMsg = details.length > 0 
        ? `Se eliminarán: ${details.join(', ')}, membresía, mensajes y suscripciones asociadas.`
        : 'Se eliminarán su membresía, mensajes y suscripciones asociadas.';
      
      showConfirmDialog(
        `Eliminar proveedor "${provider.business_name}"`,
        `${cascadeMsg} Esta acción no se puede deshacer.`,
        async () => {
          try {
            await axios.delete(`${API_URL}/api/admin/providers/${provider.id}`);
            fetchData();
            toast.success('Proveedor eliminado correctamente');
          } catch (error) {
            toast.error('Error al eliminar proveedor');
          }
        },
        'destructive'
      );
    } catch (error) {
      toast.error('Error al obtener información del proveedor');
    }
  };

  const handleToggleUserActive = async (userId) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/toggle-active`);
      fetchData();
    } catch (error) {
      console.error('Failed to toggle user:', error);
    }
  };

  const [deleteUserConfirm, setDeleteUserConfirm] = useState({ open: false, id: null, name: '' });
  const [telegramEditor, setTelegramEditor] = useState({ open: false, user: null });

  const handleDeleteUser = async () => {
    const { id, name } = deleteUserConfirm;
    setDeleteUserConfirm({ open: false, id: null, name: '' });
    try {
      await axios.delete(`${API_URL}/api/admin/users/${id}`);
      toast.success(`Usuario "${name}" eliminado`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  // Territory bulk import functions
  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/territories/template/download`, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_territorios.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Plantilla descargada correctamente');
    } catch (error) {
      console.error('Failed to download template:', error);
      toast.error('Error al descargar la plantilla');
    }
  };

  const handleImportTerritories = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Solo se permiten archivos Excel (.xlsx, .xls)');
      return;
    }

    setImportingTerritories(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/territories/bulk-import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      const stats = response.data.stats;
      toast.success('Importación completada', {
        description: `Regiones: ${stats.regions_created} creadas, ${stats.regions_existing} existentes. Comunas: ${stats.communes_created} creadas, ${stats.communes_existing} existentes.`
      });
      
      if (stats.errors?.length > 0) {
        toast.warning(`${stats.errors.length} errores durante la importación`);
      }
      
      fetchData();
    } catch (error) {
      console.error('Failed to import territories:', error);
      toast.error(error.response?.data?.detail || 'Error al importar territorios');
    } finally {
      setImportingTerritories(false);
      if (territoryFileInputRef.current) {
        territoryFileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteRegion = async (regionId, regionName) => {
    setDeleteTarget({ type: 'region', id: regionId, name: regionName });
    setDeleteDialogOpen(true);
  };

  const handleDeleteCommune = async (communeId, communeName) => {
    setDeleteTarget({ type: 'commune', id: communeId, name: communeName });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      if (deleteTarget.type === 'region') {
        await axios.delete(`${API_URL}/api/territories/regions/${deleteTarget.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success(`Región "${deleteTarget.name}" eliminada correctamente`);
      } else if (deleteTarget.type === 'commune') {
        await axios.delete(`${API_URL}/api/territories/communes/${deleteTarget.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success(`Comuna "${deleteTarget.name}" eliminada correctamente`);
      }
      fetchData();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setDeleteTarget({ type: '', id: '', name: '' });
    }
  };

  // Category CRUD
  const handleSaveCategory = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingCategory) {
        await axios.put(`${API_URL}/api/admin/categories/${editingCategory.id}`, newCategory);
      } else {
        await axios.post(`${API_URL}/api/admin/categories`, newCategory);
      }
      setCategoryDialogOpen(false);
      setEditingCategory(null);
      setNewCategory({ name: '', name_en: '', name_pt: '', icon: '', color: '', image_url: '' });
      fetchData();
      toast.success(editingCategory ? 'Categoría actualizada' : 'Categoría creada');
    } catch (error) {
      console.error('Failed to save category:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar categoría');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    showConfirmDialog(
      'Eliminar categoría',
      '¿Estás seguro de eliminar esta categoría?',
      async () => {
        try {
          await axios.delete(`${API_URL}/api/admin/categories/${categoryId}`);
          fetchData();
          toast.success('Categoría eliminada');
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Error al eliminar categoría');
        }
        closeConfirmDialog();
      },
      'destructive'
    );
  };

  // Region CRUD
  const handleSaveRegion = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingRegion) {
        await axios.put(`${API_URL}/api/admin/regions/${editingRegion.id}`, { name: newRegion.name });
      } else {
        await axios.post(`${API_URL}/api/admin/regions`, newRegion);
      }
      setRegionDialogOpen(false);
      setEditingRegion(null);
      setNewRegion({ name: '', country_id: '' });
      fetchData();
      toast.success(editingRegion ? 'Región actualizada' : 'Región creada');
    } catch (error) {
      console.error('Failed to save region:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar región');
    } finally {
      setSubmitting(false);
    }
  };

  // Commune CRUD
  const handleSaveCommune = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingCommune) {
        await axios.put(`${API_URL}/api/admin/communes/${editingCommune.id}`, { name: newCommune.name });
      } else {
        await axios.post(`${API_URL}/api/admin/communes`, newCommune);
      }
      setCommuneDialogOpen(false);
      setEditingCommune(null);
      setNewCommune({ name: '', region_id: '' });
      fetchData();
      toast.success(editingCommune ? 'Comuna actualizada' : 'Comuna creada');
    } catch (error) {
      console.error('Failed to save commune:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar comuna');
    } finally {
      setSubmitting(false);
    }
  };

  // Create User
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/admin/users`, newUser);
      setUserDialogOpen(false);
      setNewUser({ email: '', password: '', name: '', role: 'provider' });
      toast.success('Usuario creado correctamente');
      fetchData();
    } catch (error) {
      console.error('Failed to create user:', error);
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    } finally {
      setSubmitting(false);
    }
  };

  // Edit User
  const openEditUser = (user) => {
    setEditingUser({ id: user.id, name: user.name, email: user.email, role: user.role, password: '' });
    setEditUserDialogOpen(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { name: editingUser.name, email: editingUser.email, role: editingUser.role };
      if (editingUser.password?.trim()) payload.password = editingUser.password;
      await axios.put(`${API_URL}/api/admin/users/${editingUser.id}`, payload);
      setEditUserDialogOpen(false);
      setEditingUser(null);
      toast.success('Usuario actualizado correctamente');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar usuario');
    } finally {
      setSubmitting(false);
    }
  };

  // Create Provider
  const handleCreateProvider = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/admin/providers`, newProvider);
      setProviderDialogOpen(false);
      setNewProvider({ business_name: '', description: '', phone: '', website: '', commune_id: '' });
      toast.success('Proveedor creado correctamente');
      fetchData();
    } catch (error) {
      console.error('Failed to create provider:', error);
      toast.error(error.response?.data?.detail || 'Error al crear proveedor');
    } finally {
      setSubmitting(false);
    }
  };

  // Assign User to Provider
  const handleAssignUser = async () => {
    if (!selectedUserId || !selectedProvider) return;
    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/api/admin/providers/${selectedProvider.id}/assign-user`, {
        user_id: selectedUserId
      });
      toast.success('Usuario asignado correctamente');
      // Refresh data and update dialog
      await fetchData();
      // Find updated provider
      const updatedProviders = await axios.get(`${API_URL}/api/admin/providers`);
      const updatedProvider = updatedProviders.data.find(p => p.id === selectedProvider.id);
      if (updatedProvider) {
        setSelectedProvider(updatedProvider);
      }
      setSelectedUserId('');
    } catch (error) {
      console.error('Failed to assign user:', error);
      toast.error(error.response?.data?.detail || 'Error al asignar usuario');
    } finally {
      setSubmitting(false);
    }
  };

  const openAssignUserDialog = (provider) => {
    setSelectedProvider(provider);
    setSelectedUserId('');
    setAssignUserDialogOpen(true);
  };

  const openEditCategory = (cat) => {
    setEditingCategory(cat);
    setNewCategory({ name: cat.name, name_en: cat.name_en || '', name_pt: cat.name_pt || '', icon: cat.icon || '', color: cat.color || '', image_url: cat.image_url || '' });
    setCategoryDialogOpen(true);
  };

  const openEditRegion = (reg) => {
    setEditingRegion(reg);
    setNewRegion({ name: reg.name, country_id: reg.country_id });
    setRegionDialogOpen(true);
  };

  const openEditCommune = (com) => {
    setEditingCommune(com);
    setNewCommune({ name: com.name, region_id: com.region_id });
    setCommuneDialogOpen(true);
  };

  // Site logo functions
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de archivo no permitido. Solo se permiten imágenes (JPEG, PNG, GIF, WebP, SVG)');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('El archivo es muy grande. Tamaño máximo: 2MB');
      return;
    }

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/uploads/site-logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success('Logo del sitio actualizado correctamente');
      refreshLogo();
    } catch (error) {
      console.error('Failed to upload logo:', error);
      toast.error(error.response?.data?.detail || 'Error al subir el logo');
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };

  const handleDeleteLogo = async () => {
    showConfirmDialog(
      'Eliminar logo',
      '¿Estás seguro de eliminar el logo personalizado? El espacio quedará vacío.',
      async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`${API_URL}/api/uploads/site-logo`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          toast.success('Logo eliminado correctamente.');
          refreshLogo();
        } catch (error) {
          console.error('Failed to delete logo:', error);
          toast.error(error.response?.data?.detail || 'Error al eliminar el logo');
        }
        closeConfirmDialog();
      },
      'destructive'
    );
  };

  // Dark logo handlers
  const handleDarkLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDarkLogo(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API_URL}/api/uploads/dark-logo`, formData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Logo modo noche actualizado');
      refreshLogo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir logo oscuro');
    } finally {
      setUploadingDarkLogo(false);
      if (darkLogoInputRef.current) darkLogoInputRef.current.value = '';
    }
  };

  const handleDeleteDarkLogo = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/uploads/dark-logo`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Logo modo noche eliminado');
      refreshLogo();
    } catch (error) {
      toast.error('Error al eliminar logo oscuro');
    }
  };

  // Favicon handlers
  const handleFaviconUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type - only PNG
    if (file.type !== 'image/png') {
      toast.error('Solo se permiten archivos PNG para el favicon');
      return;
    }

    // Validate file size (max 500KB)
    if (file.size > 500 * 1024) {
      toast.error('El archivo es muy grande. Tamaño máximo: 500KB');
      return;
    }

    setUploadingFavicon(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/uploads/favicon`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      setFaviconUrl(response.data.url);
      toast.success('Favicon actualizado correctamente');
      // Update the actual favicon in the browser
      updateBrowserFavicon(response.data.url);
    } catch (error) {
      console.error('Failed to upload favicon:', error);
      toast.error(error.response?.data?.detail || 'Error al subir el favicon');
    } finally {
      setUploadingFavicon(false);
      if (faviconInputRef.current) {
        faviconInputRef.current.value = '';
      }
    }
  };

  const handleDeleteFavicon = async () => {
    showConfirmDialog(
      'Eliminar favicon',
      '¿Estás seguro de eliminar el favicon personalizado?',
      async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`${API_URL}/api/uploads/favicon`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          setFaviconUrl('');
          toast.success('Favicon eliminado correctamente.');
        } catch (error) {
          console.error('Failed to delete favicon:', error);
          toast.error(error.response?.data?.detail || 'Error al eliminar el favicon');
        }
        closeConfirmDialog();
      },
      'destructive'
    );
  };

  const updateBrowserFavicon = (url) => {
    const fullUrl = url.startsWith('/') ? `${API_URL}${url}` : url;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = fullUrl;
  };

  // Site info handlers
  const handleSaveSiteInfo = async () => {
    setSavingSiteInfo(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/admin/site-info`, siteInfo, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      toast.success('Información del sitio actualizada');
      setEditingSiteInfo(false);
    } catch (error) {
      console.error('Failed to save site info:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSavingSiteInfo(false);
    }
  };

  const handleHeroImageUpload = async (e, index) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingHeroIndex(index);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API_URL}/api/uploads/hero-image/${index}`, formData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const updated = [...heroImages];
      while (updated.length < 4) updated.push({ url: '', label: '' });
      updated[index] = { ...updated[index], url: response.data.url };
      setHeroImages(updated);
      toast.success('Foto de portada actualizada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir imagen');
    } finally {
      setUploadingHeroIndex(null);
      if (heroInputRefs[index]?.current) heroInputRefs[index].current.value = '';
    }
  };

  const handleDeleteHeroImage = async (index) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/uploads/hero-image/${index}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const response = await axios.get(`${API_URL}/api/uploads/hero-images`);
      setHeroImages(response.data);
      toast.success('Imagen restaurada al valor por defecto');
    } catch (error) {
      toast.error('Error al eliminar imagen');
    }
  };

  // ---- Social Proof handlers ----
  const handleSocialProofPhotoUpload = async (e, index) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingSocialIndex(index);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API_URL}/api/uploads/social-proof/photo/${index}`, formData, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const updated = [...socialProof.photos];
      updated[index] = response.data.url;
      setSocialProof({ ...socialProof, photos: updated });
      toast.success('Foto actualizada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir la foto');
    } finally {
      setUploadingSocialIndex(null);
      if (socialProofInputRefs[index]?.current) socialProofInputRefs[index].current.value = '';
    }
  };

  const handleResetSocialProofPhoto = async (index) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/uploads/social-proof/photo/${index}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const res = await axios.get(`${API_URL}/api/uploads/social-proof`);
      setSocialProof(res.data);
      toast.success('Foto restaurada por defecto');
    } catch (error) {
      toast.error('Error al restaurar la foto');
    }
  };

  const handleSaveSocialProofCount = async () => {
    const num = parseInt(socialProofCountDraft, 10);
    if (Number.isNaN(num) || num < 0) {
      toast.error('Ingresa un número válido');
      return;
    }
    setSavingSocialProofCount(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `${API_URL}/api/uploads/social-proof`,
        { count: num },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSocialProof(res.data);
      toast.success('Número actualizado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSavingSocialProofCount(false);
    }
  };

  // Fetch favicon on load
  React.useEffect(() => {
    const fetchFavicon = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/uploads/favicon`);
        if (response.data.url) {
          setFaviconUrl(response.data.url);
        }
      } catch (error) {
        // Favicon not set, ignore
      }
    };
    const fetchSiteInfo = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/admin/site-info`);
        if (response.data) {
          setSiteInfo(response.data);
        }
      } catch (error) {
        // Site info not set, use defaults
      }
    };
    const fetchHeroImages = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/uploads/hero-images`);
        setHeroImages(response.data);
      } catch (error) {
        // Hero images not set, use defaults
      }
    };
    const fetchSocialProof = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/uploads/social-proof`);
        if (response.data) {
          setSocialProof(response.data);
          setSocialProofCountDraft(String(response.data.count ?? 2500));
        }
      } catch (error) {
        // Use defaults
      }
    };
    fetchFavicon();
    fetchSiteInfo();
    fetchHeroImages();
    fetchSocialProof();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAF9] p-4 md:p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32 rounded-2xl" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAF9]" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-[#064E3B] text-white py-8 px-4 md:px-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <Badge className="mb-2 bg-green-500/20 text-green-100 border-0">Admin</Badge>
            <h1 className="text-3xl font-bold mb-2">Panel de Administración</h1>
            <p className="text-green-100">Gestiona la plataforma PlanCity</p>
          </div>
          <div className="bg-white/5 border border-white/15 rounded-2xl px-4 py-3 md:min-w-[320px]" data-testid="admin-provider-landing-card">
            <div className="text-[10px] uppercase tracking-[2px] font-bold text-[#00CC66] mb-1">Landing para captar proveedores</div>
            <div className="flex items-center gap-2">
              <code className="text-xs md:text-sm text-white/90 font-mono truncate flex-1">{`${window.location.origin}/para-proveedores`}</code>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 px-2 text-white hover:bg-white/10 rounded-lg"
                onClick={() => {
                  const url = `${window.location.origin}/para-proveedores`;
                  navigator.clipboard.writeText(url);
                  toast.success('URL copiada al portapapeles');
                }}
                title="Copiar URL"
                data-testid="admin-copy-provider-landing-url"
              >
                <Copy className="w-4 h-4" />
              </Button>
              <a
                href="/para-proveedores"
                target="_blank"
                rel="noopener noreferrer"
                className="h-8 w-8 flex items-center justify-center text-white hover:bg-white/10 rounded-lg transition-colors"
                title="Abrir en nueva pestaña"
                data-testid="admin-open-provider-landing"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
            <p className="text-[11px] text-white/50 mt-1.5">Envíalo a prospectos. Se registran y aparecen aquí para verificar.</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('users')}</p>
                  <p className="text-2xl font-bold text-[#064E3B]">{stats?.total_users || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-xl">
                  <Store className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('providers')}</p>
                  <p className="text-2xl font-bold text-[#064E3B]">{stats?.total_providers || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-xl">
                  <Calendar className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Eventos Activos</p>
                  <p className="text-2xl font-bold text-[#064E3B]">{stats?.active_events || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-yellow-100 rounded-xl">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Pendientes</p>
                  <p className="text-2xl font-bold text-[#064E3B]">{stats?.pending_events || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Visitor analytics — public-page traffic to the platform */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-testid="admin-visit-stats">
          <Card className="rounded-2xl border-0 shadow-sm" data-testid="visit-stats-total">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-emerald-100 rounded-xl shrink-0">
                  <Eye className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-500">Visitas a la plataforma</p>
                  <p className="text-2xl font-bold text-[#064E3B]" data-testid="visit-total-count">
                    {(visitStats?.total_visits ?? 0).toLocaleString('es-CL')}
                  </p>
                  <div className="mt-3 flex items-center gap-4 text-xs">
                    <span className="text-gray-500">
                      Hoy{' '}
                      <strong className="text-[#064E3B]" data-testid="visit-today-count">
                        {(visitStats?.visits_today ?? 0).toLocaleString('es-CL')}
                      </strong>
                    </span>
                    <span className="text-gray-300">·</span>
                    <span className="text-gray-500">
                      7 días{' '}
                      <strong className="text-[#064E3B]" data-testid="visit-7d-count">
                        {(visitStats?.visits_7d ?? 0).toLocaleString('es-CL')}
                      </strong>
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-0 shadow-sm" data-testid="visit-stats-unique">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-indigo-100 rounded-xl shrink-0">
                  <TrendingUp className="w-6 h-6 text-indigo-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-500">Visitantes únicos</p>
                  <p className="text-2xl font-bold text-[#064E3B]" data-testid="unique-total-count">
                    {(visitStats?.total_unique ?? 0).toLocaleString('es-CL')}
                  </p>
                  <div className="mt-3 flex items-center gap-4 text-xs">
                    <span className="text-gray-500">
                      Hoy{' '}
                      <strong className="text-[#064E3B]" data-testid="unique-today-count">
                        {(visitStats?.unique_today ?? 0).toLocaleString('es-CL')}
                      </strong>
                    </span>
                    <span className="text-gray-300">·</span>
                    <span className="text-gray-500">
                      7 días{' '}
                      <strong className="text-[#064E3B]" data-testid="unique-7d-count">
                        {(visitStats?.unique_7d ?? 0).toLocaleString('es-CL')}
                      </strong>
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar + Content Layout */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-52 shrink-0" data-testid="admin-sidebar">
            <nav className="sticky top-6 space-y-4">
              {/* Dashboard */}
              <div>
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1 px-3">Dashboard</p>
                <div className="space-y-0.5">
                  {[
                    { value: 'overview', label: 'General', icon: BarChart3 },
                    { value: 'pending', label: 'Pendientes', icon: Clock, badge: pendingEvents.length || null },
                    { value: 'event-stats', label: 'Estadísticas', icon: TrendingUp, onClick: () => fetchEventStats() },
                  ].map(item => {
                    const Icon = item.icon;
                    return (
                      <button key={item.value} onClick={() => { setActiveTab(item.value); item.onClick?.(); }} data-testid={`sidebar-${item.value}`}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === item.value ? 'bg-[#064E3B] text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}>
                        <Icon className="w-4 h-4 shrink-0" />
                        <span className="truncate">{item.label}</span>
                        {item.badge && <Badge className="ml-auto bg-yellow-500 text-white text-[10px] px-1.5 py-0">{item.badge}</Badge>}
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* Contenido */}
              <div>
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1 px-3">Contenido</p>
                <div className="space-y-0.5">
                  {[
                    { value: 'categories', label: 'Categorías', icon: Tag },
                    { value: 'territories', label: 'Territorios', icon: MapPin },
                    { value: 'venues', label: 'Lugares', icon: Landmark },
                    { value: 'ai-events', label: 'Buscar con IA', icon: Star },
                    // Organizadores + Web Scraping + Boost restricted to a single operator on purpose.
                    ...((user?.email || '').toLowerCase() === 'cristian@plancity.cl'
                      ? [
                          { value: 'organizers', label: 'Organizadores', icon: Instagram },
                          { value: 'scraping', label: 'Web Scraping', icon: Globe },
                          { value: 'boost', label: 'Boost Vistas', icon: TrendingUp },
                          { value: 'duplicates', label: 'Duplicados', icon: Copy },
                        ]
                      : []),
                  ].map(item => {
                    const Icon = item.icon;
                    return (
                      <button key={item.value} onClick={() => setActiveTab(item.value)} data-testid={`sidebar-${item.value}`}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === item.value ? 'bg-[#064E3B] text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}>
                        <Icon className="w-4 h-4 shrink-0" />
                        <span className="truncate">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* Usuarios */}
              <div>
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1 px-3">Usuarios</p>
                <div className="space-y-0.5">
                  {[
                    { value: 'users', label: t('users'), icon: Users },
                    { value: 'providers', label: t('providers'), icon: Store, badge: unseenProvidersCount || null },
                    { value: 'enganches', label: 'Enganches', icon: Link2 },
                    { value: 'messages', label: 'Mensajes', icon: Mail, badge: unreadMessagesCount || null, onClick: fetchPlatformMessages },
                  ].map(item => {
                    const Icon = item.icon;
                    return (
                      <button key={item.value} onClick={() => { setActiveTab(item.value); item.onClick?.(); }} data-testid={`sidebar-${item.value}`}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === item.value ? 'bg-[#064E3B] text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}>
                        <Icon className="w-4 h-4 shrink-0" />
                        <span className="truncate">{item.label}</span>
                        {item.badge ? (
                          <span
                            className={`ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-semibold ${activeTab === item.value ? 'bg-white text-[#064E3B]' : 'bg-red-500 text-white'}`}
                            data-testid={`sidebar-${item.value}-badge`}
                          >
                            {item.badge}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* Comercial */}
              <div>
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1 px-3">Comercial</p>
                <div className="space-y-0.5">
                  {[
                    { value: 'plans', label: 'Planes', icon: CreditCard, onClick: fetchPlans },
                    { value: 'subscriptions', label: 'Suscripciones', icon: Bell, onClick: fetchSubscriptions },
                  ].map(item => {
                    const Icon = item.icon;
                    return (
                      <button key={item.value} onClick={() => { setActiveTab(item.value); item.onClick?.(); }} data-testid={`sidebar-${item.value}`}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === item.value ? 'bg-[#064E3B] text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}>
                        <Icon className="w-4 h-4 shrink-0" />
                        <span className="truncate">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* Sistema */}
              <div>
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1 px-3">Sistema</p>
                <div className="space-y-0.5">
                  <button onClick={() => setActiveTab('settings')} data-testid="sidebar-settings"
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'settings' ? 'bg-[#064E3B] text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}>
                    <Settings className="w-4 h-4 shrink-0" />
                    <span className="truncate">Configuración</span>
                  </button>
                </div>
              </div>
            </nav>
          </aside>

          {/* Content Column */}
          <div className="flex-1 min-w-0">
            {/* Mobile Section Selector — mirrors the desktop sidebar 1:1 so
                the small-screen admin never loses access to Lugares,
                Organizadores, Web Scraping, Boost Vistas, or Duplicados. The
                operator-restricted items are hidden for non-Cristian admins,
                same as the sidebar. */}
            <div className="lg:hidden mb-4" data-testid="admin-mobile-selector">
              <Select value={activeTab} onValueChange={(v) => {
                setActiveTab(v);
                if (v === 'plans') fetchPlans();
                if (v === 'subscriptions') fetchSubscriptions();
                if (v === 'messages') fetchPlatformMessages();
                if (v === 'event-stats') fetchEventStats();
              }}>
                <SelectTrigger className="w-full bg-white rounded-xl" data-testid="admin-mobile-selector-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[70vh]">
                  <SelectGroup>
                    <SelectLabel className="text-[10px] uppercase tracking-wider text-gray-400">Dashboard</SelectLabel>
                    <SelectItem value="overview">General</SelectItem>
                    <SelectItem value="pending">Pendientes</SelectItem>
                    <SelectItem value="event-stats">Estadísticas eventos</SelectItem>
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel className="text-[10px] uppercase tracking-wider text-gray-400">Contenido</SelectLabel>
                    <SelectItem value="categories">Categorías</SelectItem>
                    <SelectItem value="territories">Territorios</SelectItem>
                    <SelectItem value="venues">Lugares</SelectItem>
                    <SelectItem value="ai-events">Buscar con IA</SelectItem>
                    {(user?.email || '').toLowerCase() === 'cristian@plancity.cl' && (
                      <>
                        <SelectItem value="organizers">Organizadores</SelectItem>
                        <SelectItem value="scraping">Web Scraping</SelectItem>
                        <SelectItem value="boost">Boost Vistas</SelectItem>
                        <SelectItem value="duplicates">Duplicados</SelectItem>
                      </>
                    )}
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel className="text-[10px] uppercase tracking-wider text-gray-400">Usuarios</SelectLabel>
                    <SelectItem value="users">{t('users')}</SelectItem>
                    <SelectItem value="providers">{t('providers')}{unseenProvidersCount ? ` (${unseenProvidersCount})` : ''}</SelectItem>
                    <SelectItem value="enganches">Enganches</SelectItem>
                    <SelectItem value="messages">Mensajes{unreadMessagesCount ? ` (${unreadMessagesCount})` : ''}</SelectItem>
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel className="text-[10px] uppercase tracking-wider text-gray-400">Comercial</SelectLabel>
                    <SelectItem value="plans">Planes</SelectItem>
                    <SelectItem value="subscriptions">Suscripciones</SelectItem>
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel className="text-[10px] uppercase tracking-wider text-gray-400">Sistema</SelectLabel>
                    <SelectItem value="settings">Configuración</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <Tabs value={activeTab}>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="rounded-2xl border-0 shadow-sm">
                <CardHeader>
                  <CardTitle>Resumen de Actividad</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                      <span className="text-gray-600">Total eventos</span>
                      <span className="font-bold text-[#064E3B]">{stats?.total_events || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                      <span className="text-gray-600">Categorías activas</span>
                      <span className="font-bold text-[#064E3B]">{stats?.total_categories || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-green-50 rounded-xl">
                      <span className="text-green-700">Eventos activos</span>
                      <span className="font-bold text-green-700">{stats?.active_events || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-yellow-50 rounded-xl">
                      <span className="text-yellow-700">Pendientes de aprobación</span>
                      <span className="font-bold text-yellow-700">{stats?.pending_events || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-2xl border-0 shadow-sm">
                <CardHeader>
                  <CardTitle>Últimos Usuarios</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {allUsers.slice(0, 5).map((user) => (
                      <div key={user.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                        <div className="w-10 h-10 bg-[#00CC66] rounded-full flex items-center justify-center">
                          <span className="text-white font-medium">
                            {user.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#064E3B] truncate">{user.name}</p>
                          <p className="text-xs text-gray-500 truncate">{user.email}</p>
                        </div>
                        <Badge variant="outline" className="rounded-full capitalize">
                          {user.role}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Event Engagement Stats Tab */}
          <TabsContent value="event-stats">
            <Card className="rounded-2xl border-0 shadow-sm" data-testid="event-stats-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 flex-wrap gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#064E3B]" />
                    Estadísticas de eventos
                  </CardTitle>
                  <CardDescription>Vistas e interacciones por evento — {eventStats.length} en total</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={eventStatsSort} onValueChange={(v) => { setEventStatsSort(v); fetchEventStats(v); }}>
                    <SelectTrigger className="w-44 rounded-full" data-testid="event-stats-sort">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="views">Más vistos</SelectItem>
                      <SelectItem value="clicks">Más interactuados</SelectItem>
                      <SelectItem value="recent">Más recientes</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" className="rounded-full" onClick={() => fetchEventStats()} disabled={loadingEventStats} data-testid="event-stats-refresh">
                    {loadingEventStats ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Actualizar'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Filters toolbar */}
                {eventStats.length > 0 && (() => {
                  // Build unique provider list from current eventStats
                  const providerMap = new Map();
                  const regionMap = new Map();
                  const communeMap = new Map();
                  for (const e of eventStats) {
                    if (e.provider_id && !providerMap.has(e.provider_id)) {
                      providerMap.set(e.provider_id, e.provider_name || '(sin nombre)');
                    }
                    if (e.region_id && !regionMap.has(e.region_id)) {
                      regionMap.set(e.region_id, e.region_name || '(sin región)');
                    }
                    if (e.commune_id && !communeMap.has(e.commune_id)) {
                      communeMap.set(e.commune_id, { name: e.commune_name || '(sin comuna)', regionId: e.region_id });
                    }
                  }
                  const providerOptions = Array.from(providerMap.entries()).sort((a,b) => a[1].localeCompare(b[1]));
                  const regionOptions = Array.from(regionMap.entries()).sort((a,b) => a[1].localeCompare(b[1]));
                  const communeOptions = Array.from(communeMap.entries())
                    .filter(([, v]) => esRegion === 'all' || v.regionId === esRegion)
                    .sort((a,b) => a[1].name.localeCompare(b[1].name));
                  return (
                    <div className="space-y-3 mb-5" data-testid="event-stats-toolbar">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="sm:col-span-2">
                          <DebouncedSearchInput
                            placeholder="Buscar por nombre o proveedor..."
                            onChange={(v) => { setEsSearch(v); setEventStatsPage(1); }}
                            className="rounded-xl bg-white h-10 text-sm w-full"
                            data-testid="event-stats-search"
                          />
                        </div>
                        <div className="relative">
                          <Calendar className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10" />
                          <Input
                            type="date"
                            value={esDate}
                            onChange={(e) => { setEsDate(e.target.value); setEventStatsPage(1); }}
                            className="pl-9 pr-8 rounded-xl bg-white h-10 text-sm w-full"
                            data-testid="event-stats-date"
                            title="Filtrar por fecha"
                          />
                          {esDate && (
                            <button
                              type="button"
                              onClick={() => { setEsDate(''); setEventStatsPage(1); }}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 w-5 h-5 flex items-center justify-center rounded-full text-sm z-10"
                              data-testid="event-stats-date-clear"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <Select value={esType} onValueChange={(v) => { setEsType(v); setEventStatsPage(1); }}>
                          <SelectTrigger className="rounded-xl bg-white h-10 text-sm w-full" data-testid="event-stats-type">
                            <SelectValue placeholder="Tipo" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos los tipos</SelectItem>
                            <SelectItem value="regular">Puntuales</SelectItem>
                            <SelectItem value="recurring">Recurrentes</SelectItem>
                            <SelectItem value="permanent">Permanentes</SelectItem>
                            <SelectItem value="online">Online</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={esStatus} onValueChange={(v) => { setEsStatus(v); setEventStatsPage(1); }}>
                          <SelectTrigger className="rounded-xl bg-white h-10 text-sm w-full" data-testid="event-stats-status">
                            <SelectValue placeholder="Estado" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos los estados</SelectItem>
                            <SelectItem value="active">Activos</SelectItem>
                            <SelectItem value="pending">Pendientes</SelectItem>
                            <SelectItem value="expired">Expirados</SelectItem>
                            <SelectItem value="rejected">Rechazados</SelectItem>
                            <SelectItem value="inactive">No activos</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={esProvider} onValueChange={(v) => { setEsProvider(v); setEventStatsPage(1); }}>
                          <SelectTrigger className="rounded-xl bg-white h-10 text-sm w-full" data-testid="event-stats-provider">
                            <SelectValue placeholder="Proveedor" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos los proveedores</SelectItem>
                            {providerOptions.map(([pid, pname]) => (
                              <SelectItem key={pid} value={pid}>{pname}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Select value={esRegion} onValueChange={(v) => { setEsRegion(v); setEsCommune('all'); setEventStatsPage(1); }}>
                          <SelectTrigger className="rounded-xl bg-white h-10 text-sm w-full" data-testid="event-stats-region">
                            <SelectValue placeholder="Región" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todas las regiones</SelectItem>
                            {regionOptions.map(([rid, rname]) => (
                              <SelectItem key={rid} value={rid}>{rname}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select value={esCommune} onValueChange={(v) => { setEsCommune(v); setEventStatsPage(1); }} disabled={communeOptions.length === 0}>
                          <SelectTrigger className="rounded-xl bg-white h-10 text-sm w-full" data-testid="event-stats-commune">
                            <SelectValue placeholder={esRegion === 'all' ? 'Comuna (todas)' : 'Comuna'} />
                          </SelectTrigger>
                          <SelectContent className="max-h-64">
                            <SelectItem value="all">Todas las comunas</SelectItem>
                            {communeOptions.map(([cid, cval]) => (
                              <SelectItem key={cid} value={cid}>{cval.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  );
                })()}

                {(() => {
                  // Compute filtered + paginated stats — uses deferred search so input stays snappy.
                  const q = deferredEsSearch.trim().toLowerCase();
                  const matchesDate = (ev, isoDate) => {
                    if (!isoDate) return true;
                    if (ev.is_permanent) return true;
                    if (ev.is_recurring && ev.recurring_mode === 'dates') {
                      const dates = ev.recurring_dates || [];
                      return dates.some((d) => (d && (d.date || d)) === isoDate);
                    }
                    if (ev.is_recurring) {
                      const start = (ev.start_date || '').slice(0, 10);
                      const end = (ev.recurring_end_date || ev.end_date || '').slice(0, 10);
                      if (!start) return true;
                      if (end && isoDate > end) return false;
                      return isoDate >= start;
                    }
                    const start = (ev.start_date || '').slice(0, 10);
                    const end = (ev.end_date || ev.start_date || '').slice(0, 10);
                    if (!start) return false;
                    if (isoDate < start) return false;
                    if (end && isoDate > end) return false;
                    return true;
                  };
                  const filteredStats = eventStats.filter((ev) => {
                    if (esStatus === 'inactive' && ev.status === 'active') return false;
                    if (esStatus !== 'all' && esStatus !== 'inactive' && ev.status !== esStatus) return false;
                    if (esType === 'recurring' && !ev.is_recurring) return false;
                    if (esType === 'permanent' && !ev.is_permanent) return false;
                    if (esType === 'online' && !ev.is_online) return false;
                    if (esType === 'regular' && (ev.is_recurring || ev.is_permanent || ev.is_online)) return false;
                    if (esProvider !== 'all' && ev.provider_id !== esProvider) return false;
                    if (esRegion !== 'all' && ev.region_id !== esRegion) return false;
                    if (esCommune !== 'all' && ev.commune_id !== esCommune) return false;
                    if (!matchesDate(ev, esDate)) return false;
                    if (!q) return true;
                    return `${ev.name || ''} ${ev.provider_name || ''}`.toLowerCase().includes(q);
                  });

                  if (loadingEventStats) {
                    return (
                      <div className="space-y-2">
                        <Skeleton className="h-12 w-full rounded-lg" />
                        <Skeleton className="h-12 w-full rounded-lg" />
                        <Skeleton className="h-12 w-full rounded-lg" />
                      </div>
                    );
                  }
                  if (eventStats.length === 0) {
                    return (
                      <div className="text-center py-12 text-gray-500" data-testid="event-stats-empty">
                        <TrendingUp className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p className="font-medium">Aún no hay eventos</p>
                      </div>
                    );
                  }
                  if (filteredStats.length === 0) {
                    return (
                      <div className="text-center py-12 text-gray-500" data-testid="event-stats-empty-filter">
                        <Search className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                        <p className="font-medium">No se encontraron eventos con esos filtros</p>
                      </div>
                    );
                  }
                  return (
                    <>
                      <div className="text-xs text-gray-500 mb-2" data-testid="event-stats-filtered-count">
                        Mostrando <b className="text-[#064E3B]">{filteredStats.length}</b> de {eventStats.length} eventos
                      </div>
                      <div className="space-y-2" data-testid="event-stats-list">
                        {filteredStats
                          .slice((eventStatsPage - 1) * EVENT_STATS_PER_PAGE, eventStatsPage * EVENT_STATS_PER_PAGE)
                          .map((ev, localIdx) => {
                            const i = (eventStatsPage - 1) * EVENT_STATS_PER_PAGE + localIdx;
                            return (
                        <div
                          key={ev.id}
                          onClick={() => openChart(ev)}
                          className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100 hover:bg-white hover:border-emerald-300 transition-colors cursor-pointer"
                          data-testid={`event-stat-row-${ev.id}`}
                          title="Ver evolución de visualizaciones"
                        >
                          <div className="w-7 h-7 rounded-full bg-[#064E3B] text-white text-xs font-bold flex items-center justify-center shrink-0">
                            {i + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <a
                                href={`/event/${ev.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="font-semibold text-[#064E3B] truncate hover:text-[#00CC66] hover:underline transition-colors"
                                data-testid={`event-stat-name-${ev.id}`}
                              >
                                {ev.name}
                              </a>
                              {ev.status === 'active' && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px] rounded-full">Activo</Badge>}
                              {ev.status === 'pending' && <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px] rounded-full">Pendiente</Badge>}
                              {ev.status === 'expired' && <Badge className="bg-gray-200 text-gray-600 border-0 text-[10px] rounded-full">Expirado</Badge>}
                              {ev.is_featured && ev.featured_type !== 'carousel' && <Badge className="bg-yellow-100 text-yellow-700 border-0 text-[10px] rounded-full">⭐ Destacado</Badge>}
                              {ev.featured_type === 'carousel' && <Badge className="bg-purple-100 text-purple-700 border-0 text-[10px] rounded-full">🎯 Imperdible</Badge>}
                            </div>
                            {ev.provider_name && <div className="text-xs text-gray-500 mt-0.5 truncate">{ev.provider_name}</div>}
                          </div>
                          <div className="flex items-center gap-4 text-sm shrink-0">
                            <div className="text-center" data-testid={`event-views-${ev.id}`}>
                              <div className="flex items-center gap-1 text-[#064E3B] font-bold tabular-nums">
                                <Eye className="w-3.5 h-3.5 text-gray-400" />
                                {(ev.views || 0).toLocaleString('es-CL')}
                              </div>
                              <div className="text-[10px] text-gray-500 uppercase tracking-wider">vistas</div>
                            </div>
                            <div className="w-px h-8 bg-gray-200" />
                            <div className="text-center" data-testid={`event-clicks-${ev.id}`}>
                              <div className="flex items-center gap-1 text-[#00CC66] font-bold tabular-nums">
                                <Zap className="w-3.5 h-3.5 text-gray-400" />
                                {(ev.clicks || 0).toLocaleString('es-CL')}
                              </div>
                              <div className="text-[10px] text-gray-500 uppercase tracking-wider">interac.</div>
                            </div>
                            <div className="w-px h-8 bg-gray-200" />
                            {/* Imperdible toggle — flips featured_type='carousel' via
                                the shared PUT /events/{id}/feature endpoint which
                                already accepts admin callers and enforces the
                                provider's carousel slot count. */}
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); toggleImperdible(ev); }}
                              disabled={imperdibleBusyId === ev.id}
                              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold border transition-colors ${
                                ev.featured_type === 'carousel'
                                  ? 'bg-purple-500 text-white border-purple-500 hover:bg-purple-600'
                                  : 'bg-white text-purple-700 border-purple-300 hover:bg-purple-50'
                              } ${imperdibleBusyId === ev.id ? 'opacity-60 cursor-wait' : ''}`}
                              title={ev.featured_type === 'carousel' ? 'Quitar de Imperdibles' : 'Marcar como Imperdible'}
                              data-testid={`event-imperdible-toggle-${ev.id}`}
                            >
                              {imperdibleBusyId === ev.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <GalleryHorizontal className="w-3 h-3" />
                              )}
                              Imperdible
                            </button>
                          </div>
                        </div>
                            );
                          })}
                      </div>

                      {/* Pagination */}
                      {filteredStats.length > EVENT_STATS_PER_PAGE && (
                        <div className="flex items-center justify-between mt-5 pt-4 border-t border-gray-100" data-testid="event-stats-pagination">
                          <span className="text-xs text-gray-500">
                            Mostrando {(eventStatsPage - 1) * EVENT_STATS_PER_PAGE + 1}–
                            {Math.min(eventStatsPage * EVENT_STATS_PER_PAGE, filteredStats.length)} de {filteredStats.length}
                          </span>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-full"
                              disabled={eventStatsPage === 1}
                              onClick={() => setEventStatsPage(p => Math.max(1, p - 1))}
                              data-testid="event-stats-prev"
                            >
                              <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
                            </Button>
                            <span className="text-xs font-semibold text-[#064E3B] px-2" data-testid="event-stats-page-indicator">
                              Página {eventStatsPage} de {Math.max(1, Math.ceil(filteredStats.length / EVENT_STATS_PER_PAGE))}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-full"
                              disabled={eventStatsPage >= Math.ceil(filteredStats.length / EVENT_STATS_PER_PAGE)}
                              onClick={() => setEventStatsPage(p => Math.min(Math.ceil(filteredStats.length / EVENT_STATS_PER_PAGE), p + 1))}
                              data-testid="event-stats-next"
                            >
                              Siguiente <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pending Events Tab */}
          <TabsContent value="pending">
            <Card className="rounded-2xl border-0 shadow-sm">
              <CardHeader>
                <CardTitle>{t('pendingApproval')}</CardTitle>
              </CardHeader>
              <CardContent>
                {pendingEvents.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-12 h-12 mx-auto text-green-300 mb-4" />
                    <p className="text-gray-500">No hay eventos pendientes</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingEvents.map((event) => (
                      <div
                        key={event.id}
                        className="flex items-center gap-4 p-4 bg-yellow-50 rounded-xl border border-yellow-100"
                        data-testid={`pending-event-${event.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-[#064E3B]">{event.name}</h3>
                          <p className="text-sm text-gray-500">
                            Por: {event.provider_name} • {event.is_permanent ? 'Permanente' : event.is_recurring ? 'Recurrente' : formatChile(event.start_date, "d MMM yyyy")}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="bg-green-500 hover:bg-green-600 text-white rounded-full"
                            onClick={() => handleApproveEvent(event.id)}
                            data-testid={`approve-event-${event.id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            {t('approve')}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-500 border-red-200 hover:bg-red-50 rounded-full"
                            onClick={() => handleRejectEvent(event.id)}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            {t('reject')}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories">
            <Card className="rounded-2xl border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Categorías</CardTitle>
                <Dialog open={categoryDialogOpen} onOpenChange={(open) => {
                  setCategoryDialogOpen(open);
                  if (!open) {
                    setEditingCategory(null);
                    setNewCategory({ name: '', name_en: '', name_pt: '', icon: '', color: '', image_url: '' });
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary" data-testid="add-category-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Nueva Categoría
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingCategory ? 'Editar' : 'Nueva'} Categoría</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSaveCategory} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Nombre (ES) *</Label>
                        <Input
                          value={newCategory.name}
                          onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Nombre (EN)</Label>
                          <Input
                            value={newCategory.name_en}
                            onChange={(e) => setNewCategory({ ...newCategory, name_en: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Nombre (PT)</Label>
                          <Input
                            value={newCategory.name_pt}
                            onChange={(e) => setNewCategory({ ...newCategory, name_pt: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Ícono</Label>
                          <Input
                            placeholder="music, palette, utensils..."
                            value={newCategory.icon}
                            onChange={(e) => setNewCategory({ ...newCategory, icon: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Color</Label>
                          <Input
                            type="color"
                            value={newCategory.color || '#00CC66'}
                            onChange={(e) => setNewCategory({ ...newCategory, color: e.target.value })}
                          />
                        </div>
                      </div>
                      {/* Category Image */}
                      <div className="space-y-2">
                        <Label>Imagen de categoría</Label>
                        <div className="flex gap-2">
                          <Input
                            placeholder="URL de imagen o sube un archivo"
                            value={newCategory.image_url || ''}
                            onChange={(e) => setNewCategory({ ...newCategory, image_url: e.target.value })}
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.accept = 'image/*';
                              input.onchange = async (e) => {
                                const file = e.target.files[0];
                                if (!file) return;
                                if (file.size > 2 * 1024 * 1024) {
                                  toast.error('Máximo 2MB');
                                  return;
                                }
                                const formData = new FormData();
                                formData.append('file', file);
                                try {
                                  const res = await axios.post(`${API_URL}/api/uploads/category-image`, formData);
                                  setNewCategory(prev => ({ ...prev, image_url: res.data.url }));
                                  toast.success('Imagen subida');
                                } catch (err) {
                                  toast.error('Error al subir imagen');
                                }
                              };
                              input.click();
                            }}
                          >
                            <Camera className="w-4 h-4" />
                          </Button>
                        </div>
                        {newCategory.image_url && (
                          <div className="relative rounded-lg overflow-hidden h-24 bg-gray-100">
                            <img
                              src={newCategory.image_url.startsWith('/api/') ? `${API_URL}${newCategory.image_url}` : newCategory.image_url}
                              alt="Preview"
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          </div>
                        )}
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => setCategoryDialogOpen(false)}>
                          Cancelar
                        </Button>
                        <Button type="submit" className="btn-primary" disabled={submitting}>
                          {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                          {editingCategory ? 'Guardar' : 'Crear'}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {categories.map((cat) => (
                    <div key={cat.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                      {cat.image_url ? (
                        <div className="w-14 h-10 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={cat.image_url.startsWith('/api/') ? `${API_URL}${cat.image_url}` : cat.image_url}
                            alt={cat.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div 
                          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: cat.color || '#00CC66' }}
                        >
                          <Tag className="w-5 h-5 text-white" />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="font-semibold text-[#064E3B]">{cat.name}</p>
                        <p className="text-sm text-gray-500">{cat.events_count} eventos</p>
                      </div>
                      <Badge className={`${cat.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'} border-0 rounded-full`}>
                        {cat.is_active ? 'Activa' : 'Inactiva'}
                      </Badge>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" className="rounded-full" onClick={() => openEditCategory(cat)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="rounded-full text-red-500 hover:bg-red-50"
                          onClick={() => handleDeleteCategory(cat.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Territories Tab */}
          <TabsContent value="territories">
            {/* Import/Export Card */}
            <Card className="rounded-2xl border-0 shadow-sm mb-6">
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <FileSpreadsheet className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#064E3B]">Carga Masiva de Territorios</h3>
                      <p className="text-sm text-gray-500">Importa regiones y comunas desde un archivo Excel</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      className="rounded-full"
                      onClick={handleDownloadTemplate}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Descargar Plantilla
                    </Button>
                    <input
                      ref={territoryFileInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleImportTerritories}
                      className="hidden"
                      id="territory-import"
                    />
                    <Button
                      className="btn-primary"
                      onClick={() => territoryFileInputRef.current?.click()}
                      disabled={importingTerritories}
                    >
                      {importingTerritories ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      {importingTerritories ? 'Importando...' : 'Importar Excel'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Regions */}
              <Card className="rounded-2xl border-0 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="w-5 h-5" />
                    Regiones ({regions.length})
                  </CardTitle>
                  <Dialog open={regionDialogOpen} onOpenChange={(open) => {
                    setRegionDialogOpen(open);
                    if (!open) {
                      setEditingRegion(null);
                      setNewRegion({ name: '', country_id: '' });
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="btn-secondary">
                        <Plus className="w-4 h-4 mr-1" />
                        Nueva
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{editingRegion ? 'Editar' : 'Nueva'} Región</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleSaveRegion} className="space-y-4">
                        <div className="space-y-2">
                          <Label>Nombre *</Label>
                          <Input
                            value={newRegion.name}
                            onChange={(e) => setNewRegion({ ...newRegion, name: e.target.value })}
                            required
                          />
                        </div>
                        {!editingRegion && (
                          <div className="space-y-2">
                            <Label>País *</Label>
                            <Select
                              value={newRegion.country_id}
                              onValueChange={(v) => setNewRegion({ ...newRegion, country_id: v })}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Seleccionar país" />
                              </SelectTrigger>
                              <SelectContent>
                                {countries.map(c => (
                                  <SelectItem key={c.id} value={c.id}>{c.flag_emoji} {c.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        <div className="flex justify-end gap-2">
                          <Button type="button" variant="outline" onClick={() => setRegionDialogOpen(false)}>
                            Cancelar
                          </Button>
                          <Button type="submit" className="btn-primary" disabled={submitting}>
                            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            {editingRegion ? 'Guardar' : 'Crear'}
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {regions.map((reg) => (
                      <div key={reg.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl group">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#064E3B] truncate">{reg.name}</p>
                          <p className="text-xs text-gray-500">{reg.communes_count} comunas • {reg.events_count} eventos</p>
                        </div>
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" className="rounded-full h-8 w-8" onClick={() => openEditRegion(reg)}>
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className={`rounded-full h-8 w-8 ${reg.events_count > 0 ? 'opacity-30 cursor-not-allowed' : 'text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100'}`}
                            onClick={() => reg.events_count === 0 && handleDeleteRegion(reg.id, reg.name)}
                            disabled={reg.events_count > 0}
                            title={reg.events_count > 0 ? 'No se puede eliminar: tiene eventos asociados' : 'Eliminar región'}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Communes */}
              <Card className="rounded-2xl border-0 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    Comunas ({communes.length})
                  </CardTitle>
                  <Dialog open={communeDialogOpen} onOpenChange={(open) => {
                    setCommuneDialogOpen(open);
                    if (!open) {
                      setEditingCommune(null);
                      setNewCommune({ name: '', region_id: '' });
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="btn-secondary">
                        <Plus className="w-4 h-4 mr-1" />
                        Nueva
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{editingCommune ? 'Editar' : 'Nueva'} Comuna</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleSaveCommune} className="space-y-4">
                        <div className="space-y-2">
                          <Label>Nombre *</Label>
                          <Input
                            value={newCommune.name}
                            onChange={(e) => setNewCommune({ ...newCommune, name: e.target.value })}
                            required
                          />
                        </div>
                        {!editingCommune && (
                          <div className="space-y-2">
                            <Label>Región *</Label>
                            <Select
                              value={newCommune.region_id}
                              onValueChange={(v) => setNewCommune({ ...newCommune, region_id: v })}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Seleccionar región" />
                              </SelectTrigger>
                              <SelectContent>
                                {regions.map(r => (
                                  <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        <div className="flex justify-end gap-2">
                          <Button type="button" variant="outline" onClick={() => setCommuneDialogOpen(false)}>
                            Cancelar
                          </Button>
                          <Button type="submit" className="btn-primary" disabled={submitting}>
                            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            {editingCommune ? 'Guardar' : 'Crear'}
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {communes.map((com) => (
                      <div key={com.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl group">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#064E3B] truncate">{com.name}</p>
                          <p className="text-xs text-gray-500">{com.region_name} • {com.events_count} eventos</p>
                        </div>
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" className="rounded-full h-8 w-8" onClick={() => openEditCommune(com)}>
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className={`rounded-full h-8 w-8 ${com.events_count > 0 ? 'opacity-30 cursor-not-allowed' : 'text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100'}`}
                            onClick={() => com.events_count === 0 && handleDeleteCommune(com.id, com.name)}
                            disabled={com.events_count > 0}
                            title={com.events_count > 0 ? 'No se puede eliminar: tiene eventos asociados' : 'Eliminar comuna'}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="rounded-2xl border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{t('users')}</CardTitle>
                <Dialog open={userDialogOpen} onOpenChange={(open) => {
                  setUserDialogOpen(open);
                  if (!open) setNewUser({ email: '', password: '', name: '', role: 'provider' });
                }}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary" data-testid="add-user-btn">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Nuevo Usuario
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Crear Usuario</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateUser} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Nombre *</Label>
                        <Input
                          value={newUser.name}
                          onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                          placeholder="Nombre completo"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          value={newUser.email}
                          onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                          placeholder="correo@ejemplo.com"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Contraseña *</Label>
                        <Input
                          type="password"
                          value={newUser.password}
                          onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                          placeholder="Mínimo 6 caracteres"
                          required
                          minLength={6}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Tipo de Usuario *</Label>
                        <Select
                          value={newUser.role}
                          onValueChange={(v) => setNewUser({ ...newUser, role: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="provider">Proveedor</SelectItem>
                            <SelectItem value="admin">Administrador</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex justify-end gap-2 pt-4">
                        <Button type="button" variant="outline" onClick={() => setUserDialogOpen(false)}>
                          Cancelar
                        </Button>
                        <Button type="submit" className="btn-primary" disabled={submitting}>
                          {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                          Crear Usuario
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {allUsers.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl"
                      data-testid={`user-${user.id}`}
                    >
                      <div className="w-12 h-12 bg-[#00CC66] rounded-full flex items-center justify-center">
                        <span className="text-white font-bold">
                          {user.name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-[#064E3B]">{user.name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                        <div className="flex gap-4 text-xs text-gray-400">
                          <span>Creado: {formatDateChile(user.created_at)}</span>
                          <span>Última conexión: {user.last_login ? formatDateChile(user.last_login) : 'Nunca'}</span>
                        </div>
                        {user.role === 'admin' && (
                          <div
                            className="mt-1.5 flex items-center gap-2 text-xs"
                            data-testid={`telegram-info-${user.id}`}
                          >
                            <span className="font-medium text-gray-600">📱 Telegram:</span>
                            {user.telegram_chat_id ? (
                              <>
                                <code
                                  className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 font-mono"
                                  data-testid={`telegram-chat-id-${user.id}`}
                                >
                                  {user.telegram_chat_id}
                                </code>
                                {user.telegram_notifications_enabled ? (
                                  <span
                                    className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 border border-green-200 font-semibold"
                                    data-testid={`telegram-status-${user.id}`}
                                  >
                                    🟢 Activo
                                  </span>
                                ) : (
                                  <span
                                    className="px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 border border-gray-300 font-semibold"
                                    data-testid={`telegram-status-${user.id}`}
                                  >
                                    ⚪ Pausado
                                  </span>
                                )}
                              </>
                            ) : (
                              <span
                                className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200 font-semibold"
                                data-testid={`telegram-status-${user.id}`}
                              >
                                ⚠️ Sin chat_id
                              </span>
                            )}
                            <button
                              onClick={() => setTelegramEditor({ open: true, user })}
                              className="text-emerald-700 hover:underline"
                              data-testid={`telegram-edit-${user.id}`}
                            >
                              Editar
                            </button>
                          </div>
                        )}
                      </div>
                      <Badge variant="outline" className={`rounded-full capitalize ${user.role === 'admin' ? 'border-purple-300 text-purple-700' : 'border-blue-300 text-blue-700'}`}>
                        {user.role === 'admin' ? 'Admin' : user.role === 'provider' ? 'Proveedor' : 'Usuario'}
                      </Badge>
                      <Badge className={`${user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} border-0 rounded-full`}>
                        {user.is_active ? 'Activo' : 'Inactivo'}
                      </Badge>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full"
                        onClick={() => openEditUser(user)}
                        data-testid={`edit-user-${user.id}`}
                      >
                        <Edit2 className="w-3 h-3 mr-1" />
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full"
                        onClick={() => handleToggleUserActive(user.id)}
                      >
                        {user.is_active ? 'Desactivar' : 'Activar'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600"
                        onClick={() => setDeleteUserConfirm({ open: true, id: user.id, name: user.name })}
                        data-testid={`delete-user-${user.id}`}
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Eliminar
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Delete User Confirm Dialog */}
            <AlertDialog open={deleteUserConfirm.open} onOpenChange={(open) => !open && setDeleteUserConfirm({ open: false, id: null, name: '' })}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Eliminar usuario</AlertDialogTitle>
                  <AlertDialogDescription>
                    ¿Estás seguro de eliminar al usuario <strong>"{deleteUserConfirm.name}"</strong>? Esta acción no se puede deshacer.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700 text-white">
                    Eliminar
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {/* Edit User Dialog */}
            <Dialog open={editUserDialogOpen} onOpenChange={(open) => {
              setEditUserDialogOpen(open);
              if (!open) setEditingUser(null);
            }}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Editar Usuario</DialogTitle>
                </DialogHeader>
                {editingUser && (
                  <form onSubmit={handleUpdateUser} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Nombre *</Label>
                      <Input
                        value={editingUser.name}
                        onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                        required
                        data-testid="edit-user-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={editingUser.email}
                        onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                        required
                        data-testid="edit-user-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Tipo de Usuario *</Label>
                      <Select
                        value={editingUser.role}
                        onValueChange={(v) => setEditingUser({ ...editingUser, role: v })}
                      >
                        <SelectTrigger data-testid="edit-user-role">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="provider">Proveedor</SelectItem>
                          <SelectItem value="admin">Administrador</SelectItem>
                          <SelectItem value="referrer">Enganche</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Nueva Contraseña</Label>
                      <Input
                        type="password"
                        value={editingUser.password}
                        onChange={(e) => setEditingUser({ ...editingUser, password: e.target.value })}
                        placeholder="Dejar vacío para no cambiar"
                        minLength={6}
                        data-testid="edit-user-password"
                      />
                      <p className="text-xs text-gray-400">Solo completar si desea cambiar la contraseña</p>
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                      <Button type="button" variant="outline" onClick={() => setEditUserDialogOpen(false)}>
                        Cancelar
                      </Button>
                      <Button type="submit" className="btn-primary" disabled={submitting} data-testid="save-edit-user-btn">
                        {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Guardar Cambios
                      </Button>
                    </div>
                  </form>
                )}
              </DialogContent>
            </Dialog>

            {/* Telegram wiring editor for admin users */}
            <Dialog open={telegramEditor.open} onOpenChange={(open) => !open && setTelegramEditor({ open: false, user: null })}>
              <DialogContent data-testid="telegram-editor-dialog">
                <DialogHeader>
                  <DialogTitle>Telegram — {telegramEditor.user?.name}</DialogTitle>
                </DialogHeader>
                {telegramEditor.user && (
                  <TelegramEditorForm
                    user={telegramEditor.user}
                    apiUrl={API_URL}
                    onClose={() => setTelegramEditor({ open: false, user: null })}
                    onSaved={(patch) => {
                      setAllUsers((prev) => prev.map((u) => u.id === telegramEditor.user.id ? { ...u, ...patch } : u));
                      setTelegramEditor({ open: false, user: null });
                    }}
                  />
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>
          <TabsContent value="providers">
            <Card className="rounded-2xl border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{t('providers')}</CardTitle>
                <Dialog open={providerDialogOpen} onOpenChange={(open) => {
                  setProviderDialogOpen(open);
                  if (!open) setNewProvider({ business_name: '', description: '', phone: '', website: '', commune_id: '' });
                }}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary" data-testid="add-provider-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Nuevo Proveedor
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Crear Proveedor</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateProvider} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Nombre Comercial *</Label>
                        <Input
                          value={newProvider.business_name}
                          onChange={(e) => setNewProvider({ ...newProvider, business_name: e.target.value })}
                          placeholder="Nombre del negocio"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Descripción</Label>
                        <Textarea
                          value={newProvider.description}
                          onChange={(e) => setNewProvider({ ...newProvider, description: e.target.value })}
                          placeholder="Descripción del negocio..."
                          rows={3}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Teléfono</Label>
                          <Input
                            type="tel"
                            value={newProvider.phone}
                            onChange={(e) => setNewProvider({ ...newProvider, phone: e.target.value })}
                            placeholder="+56 9 1234 5678"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Sitio Web</Label>
                          <Input
                            value={newProvider.website}
                            onChange={(e) => setNewProvider({ ...newProvider, website: e.target.value })}
                            placeholder="www.ejemplo.com"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Comuna</Label>
                        <Select
                          value={newProvider.commune_id}
                          onValueChange={(v) => setNewProvider({ ...newProvider, commune_id: v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar comuna" />
                          </SelectTrigger>
                          <SelectContent>
                            {communes.map(c => (
                              <SelectItem key={c.id} value={c.id}>{c.name} ({c.region_name})</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex justify-end gap-2 pt-4">
                        <Button type="button" variant="outline" onClick={() => setProviderDialogOpen(false)}>
                          Cancelar
                        </Button>
                        <Button type="submit" className="btn-primary" disabled={submitting}>
                          {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                          Crear Proveedor
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {allProviders.length === 0 ? (
                  <div className="text-center py-12">
                    <Store className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">No hay proveedores registrados</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {allProviders.map((provider) => (
                      <div
                        key={provider.id}
                        className={`p-5 rounded-2xl border transition-colors ${
                          provider.seen_by_admin === false
                            ? 'bg-amber-50/70 border-amber-200 ring-1 ring-amber-200'
                            : 'bg-gray-50 border-gray-100'
                        }`}
                        data-testid={`provider-${provider.id}`}
                      >
                        {/* Row 1: Avatar + Name + Plan badge */}
                        <div className="flex items-start gap-4 mb-3">
                          <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center overflow-hidden shrink-0">
                            {provider.logo_url ? (
                              <img src={provider.logo_url.startsWith('/api') ? `${API_URL}${provider.logo_url}` : provider.logo_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <Store className="w-7 h-7 text-purple-600" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-bold text-[#064E3B] text-lg">{provider.business_name}</h4>
                              {provider.seen_by_admin === false && (
                                <Badge
                                  className="bg-amber-500 text-white border-0 rounded-full text-[10px] uppercase tracking-wide font-semibold animate-pulse"
                                  data-testid={`provider-nuevo-${provider.id}`}
                                >
                                  Nuevo
                                </Badge>
                              )}
                              <Select
                                value={provider.membership_plan || 'basic'}
                                onValueChange={(v) => handleChangeProviderPlan(provider.id, v)}
                              >
                                <SelectTrigger className={`w-[130px] h-7 rounded-full text-xs font-semibold border ${
                                  (provider.membership_plan || 'basic') === 'premium' ? 'bg-gradient-to-r from-amber-50 to-yellow-50 text-amber-800 border-amber-300' :
                                  (provider.membership_plan || 'basic') === 'pro' ? 'bg-blue-50 text-blue-700 border-blue-300' :
                                  'bg-gray-100 text-gray-600 border-gray-300'
                                }`} data-testid={`plan-select-${provider.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="basic">BASIC</SelectItem>
                                  <SelectItem value="pro">PRO</SelectItem>
                                  <SelectItem value="premium">PREMIUM</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="text-sm text-gray-500 mt-0.5">
                              {provider.assigned_users && provider.assigned_users.length > 0 ? (
                                <span className="flex items-center gap-1 flex-wrap">
                                  <Users className="w-3 h-3" />
                                  {provider.assigned_users.map(u => u.name).join(', ')}
                                </span>
                              ) : (
                                <span className="text-yellow-600 text-xs">Sin usuarios asignados</span>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 mt-0.5">Creado: {formatDateChile(provider.created_at)}</p>
                          </div>
                        </div>

                        {/* Row 2: Badges (derived from plan) */}
                        <div className="flex flex-wrap gap-2 mb-3 pl-[72px]">
                          <Badge className={`${provider.is_verified ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'} border-0 rounded-full text-xs`}>
                            {provider.is_verified ? '✓ Verificado' : 'Sin verificar'}
                          </Badge>
                          {['pro', 'premium'].includes(provider.membership_plan) && (
                            <Badge className="bg-purple-100 text-purple-700 border-0 rounded-full text-xs">
                              <Star className="w-3 h-3 mr-1 fill-current" />
                              Organizador activo
                            </Badge>
                          )}
                          {provider.membership_plan === 'premium' && (
                            <Badge className="bg-amber-100 text-amber-700 border-0 rounded-full text-xs">
                              <Star className="w-3 h-3 mr-1 fill-current" />
                              Partner PlanCity
                            </Badge>
                          )}
                          {provider.trial_ends_at && provider.trial_days_remaining > 0 && (
                            <Badge className="bg-orange-100 text-orange-700 border-0 rounded-full text-xs" data-testid={`trial-badge-${provider.id}`}>
                              <Clock className="w-3 h-3 mr-1" />
                              Trial {provider.trial_plan?.toUpperCase()}: {provider.trial_days_remaining}d
                            </Badge>
                          )}
                          {(provider.bonus_featured_slots > 0 || provider.bonus_carousel_slots > 0) && (
                            <Badge className="bg-purple-100 text-purple-700 border-0 rounded-full text-xs" data-testid={`bonus-badge-${provider.id}`}>
                              <Zap className="w-3 h-3 mr-1" />
                              +{provider.bonus_featured_slots || 0} dest · +{provider.bonus_carousel_slots || 0} imp
                            </Badge>
                          )}
                        </div>

                        {/* Row 3: Actions */}
                        <div className="flex flex-wrap gap-2 pl-[72px]">
                          <Button size="sm" variant="outline" className="rounded-full text-xs h-8" onClick={() => openAssignUserDialog(provider)} title="Asignar usuario">
                            <UserPlus className="w-3.5 h-3.5 mr-1" /> Asignar
                          </Button>
                          {!provider.is_verified && (
                            <Button size="sm" className="btn-primary text-xs h-8" onClick={() => handleVerifyProvider(provider.id)}>
                              Verificar
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className={`rounded-full text-xs h-8 ${provider.trial_ends_at ? 'border-orange-300 text-orange-700 hover:bg-orange-50' : 'border-[#00CC66]/30 text-[#00CC66] hover:bg-[#00CC66]/5'}`}
                            onClick={() => openTrialDialog(provider)}
                            data-testid={`trial-btn-${provider.id}`}
                            title="Activar trial gratis"
                          >
                            <Clock className="w-3.5 h-3.5 mr-1" /> {provider.trial_ends_at ? `Trial: ${provider.trial_days_remaining}d` : 'Activar trial'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className={`rounded-full text-xs h-8 ${(provider.bonus_featured_slots > 0 || provider.bonus_carousel_slots > 0) ? 'border-purple-300 text-purple-700 hover:bg-purple-50' : 'border-purple-200 text-purple-600 hover:bg-purple-50'}`}
                            onClick={() => openBonusDialog(provider)}
                            data-testid={`bonus-btn-${provider.id}`}
                            title="Asignar slots extra de destacado/imperdible"
                          >
                            <Zap className="w-3.5 h-3.5 mr-1" /> Slots extra
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full text-xs h-8 border-red-200 text-red-500 hover:bg-red-50 ml-auto"
                            onClick={() => handleDeleteProvider(provider)}
                            data-testid={`delete-provider-${provider.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
                          </Button>
                        </div>

                        {/* Row 4: Mark as seen (only when NUEVO) */}
                        {provider.seen_by_admin === false && (
                          <div className="pt-3 mt-3 border-t border-amber-200/60 flex justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-full text-xs h-7 border-amber-300 text-amber-700 hover:bg-amber-100"
                              onClick={() => handleMarkProviderSeen(provider.id)}
                              data-testid={`mark-seen-${provider.id}`}
                            >
                              <CheckCircle className="w-3.5 h-3.5 mr-1" /> Marcar como visto
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Trial Dialog */}
            <Dialog open={trialDialogOpen} onOpenChange={(open) => {
              setTrialDialogOpen(open);
              if (!open) setTrialProvider(null);
            }}>
              <DialogContent className="max-w-md" data-testid="trial-dialog">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-[#00CC66]" />
                    Trial para {trialProvider?.business_name}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                  {trialProvider?.trial_ends_at && (
                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-sm text-orange-800">
                      <div className="font-bold mb-0.5">Trial activo</div>
                      <div>Quedan <strong>{trialProvider.trial_days_remaining} día{trialProvider.trial_days_remaining === 1 ? '' : 's'}</strong> del plan <strong>{trialProvider.trial_plan?.toUpperCase()}</strong>.</div>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-semibold text-gray-700 block mb-1.5">Plan durante el trial</label>
                    <Select value={trialPlanKey} onValueChange={setTrialPlanKey}>
                      <SelectTrigger className="rounded-xl" data-testid="trial-plan-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pro">PRO</SelectItem>
                        <SelectItem value="premium">PREMIUM</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700 block mb-1.5">Días gratis</label>
                    <div className="flex gap-2 mb-2 flex-wrap">
                      {[7, 14, 30, 60, 90].map(d => (
                        <button key={d}
                                type="button"
                                onClick={() => setTrialDays(d)}
                                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${trialDays === d ? 'bg-[#00CC66] text-white border-[#00CC66]' : 'bg-white border-gray-300 text-gray-700 hover:border-[#00CC66]'}`}
                                data-testid={`trial-preset-${d}`}>
                          {d} días
                        </button>
                      ))}
                    </div>
                    <Input type="number" min="1" max="365" value={trialDays}
                           onChange={(e) => setTrialDays(e.target.value)}
                           className="rounded-xl"
                           placeholder="Cantidad de días"
                           data-testid="trial-days-input" />
                  </div>
                  <div className="text-xs text-gray-500 leading-relaxed bg-gray-50 border border-gray-200 rounded-xl p-3">
                    💡 El proveedor verá una cuenta regresiva en su panel. Cuando termine el trial, su plan vuelve a Basic automáticamente (la fecha de expiración no bloquea publicaciones existentes).
                  </div>
                </div>
                <div className="flex gap-2 justify-end pt-2">
                  {trialProvider?.trial_ends_at && (
                    <Button variant="outline" className="rounded-full border-red-200 text-red-500 hover:bg-red-50"
                            onClick={handleCancelTrial}
                            data-testid="trial-cancel-btn">
                      Cancelar trial
                    </Button>
                  )}
                  <Button variant="outline" className="rounded-full" onClick={() => setTrialDialogOpen(false)}>
                    Cerrar
                  </Button>
                  <Button className="bg-[#00CC66] hover:bg-[#00b359] text-white rounded-full"
                          onClick={handleSaveTrial}
                          data-testid="trial-save-btn">
                    {trialProvider?.trial_ends_at ? 'Actualizar' : 'Activar trial'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Bonus Slots Dialog */}
            <Dialog open={bonusDialogOpen} onOpenChange={(open) => {
              setBonusDialogOpen(open);
              if (!open) setBonusProvider(null);
            }}>
              <DialogContent className="max-w-md" data-testid="bonus-dialog">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-purple-600" />
                    Slots extra para {bonusProvider?.business_name}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 text-sm text-purple-800">
                    Estos slots se <strong>suman al límite del plan</strong>.<br/>
                    Plan actual: <strong>{(bonusProvider?.membership_plan || 'basic').toUpperCase()}</strong>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-700 block mb-1.5 flex items-center gap-2">
                      <Star className="w-4 h-4 text-orange-500" /> Destacados extra
                    </label>
                    <div className="flex gap-2 mb-2 flex-wrap">
                      {[0, 1, 3, 5, 10].map(d => (
                        <button key={d}
                                type="button"
                                onClick={() => setBonusFeatured(d)}
                                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${parseInt(bonusFeatured, 10) === d ? 'bg-orange-500 text-white border-orange-500' : 'bg-white border-gray-300 text-gray-700 hover:border-orange-400'}`}
                                data-testid={`bonus-featured-preset-${d}`}>
                          +{d}
                        </button>
                      ))}
                    </div>
                    <Input type="number" min="0" max="100" value={bonusFeatured}
                           onChange={(e) => setBonusFeatured(e.target.value)}
                           className="rounded-xl"
                           placeholder="Cantidad de destacados extra"
                           data-testid="bonus-featured-input" />
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-700 block mb-1.5 flex items-center gap-2">
                      <GalleryHorizontal className="w-4 h-4 text-purple-500" /> Imperdibles extra
                    </label>
                    <div className="flex gap-2 mb-2 flex-wrap">
                      {[0, 1, 3, 5, 10].map(d => (
                        <button key={d}
                                type="button"
                                onClick={() => setBonusCarousel(d)}
                                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${parseInt(bonusCarousel, 10) === d ? 'bg-purple-500 text-white border-purple-500' : 'bg-white border-gray-300 text-gray-700 hover:border-purple-400'}`}
                                data-testid={`bonus-carousel-preset-${d}`}>
                          +{d}
                        </button>
                      ))}
                    </div>
                    <Input type="number" min="0" max="100" value={bonusCarousel}
                           onChange={(e) => setBonusCarousel(e.target.value)}
                           className="rounded-xl"
                           placeholder="Cantidad de imperdibles extra"
                           data-testid="bonus-carousel-input" />
                  </div>

                  <div className="text-xs text-gray-500 leading-relaxed bg-gray-50 border border-gray-200 rounded-xl p-3">
                    💡 Estos slots se reflejan automáticamente al proveedor en el contador de "Mis Eventos". Para quitarlos, deja ambos en 0.
                  </div>
                </div>
                <div className="flex gap-2 justify-end pt-2">
                  <Button variant="outline" className="rounded-full" onClick={() => setBonusDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button className="bg-purple-500 hover:bg-purple-600 text-white rounded-full"
                          onClick={handleSaveBonus}
                          data-testid="bonus-save-btn">
                    Guardar
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Assign User Dialog */}
            <Dialog open={assignUserDialogOpen} onOpenChange={(open) => {
              setAssignUserDialogOpen(open);
              if (!open) {
                setSelectedProvider(null);
                setSelectedUserId('');
              }
            }}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Gestionar Usuarios de {selectedProvider?.business_name}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {/* Current assigned users */}
                  {selectedProvider?.assigned_users?.length > 0 && (
                    <div className="space-y-2">
                      <Label>Usuarios Asignados</Label>
                      <div className="space-y-2">
                        {selectedProvider.assigned_users.map(u => (
                          <div key={u.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                            <span className="text-sm">{u.name} ({u.email})</span>
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-full text-red-500 hover:bg-red-50 h-7"
                              onClick={async () => {
                                try {
                                  await axios.put(`${API_URL}/api/admin/providers/${selectedProvider.id}/unassign-user`, {
                                    user_id: u.id
                                  });
                                  toast.success(`Usuario ${u.name} desasignado`);
                                  fetchData();
                                  // Update local state
                                  setSelectedProvider(prev => ({
                                    ...prev,
                                    assigned_users: prev.assigned_users.filter(au => au.id !== u.id)
                                  }));
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || 'Error al desasignar');
                                }
                              }}
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Quitar
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add new user */}
                  <div className="space-y-2">
                    <Label>Agregar Usuario</Label>
                    <Select
                      value={selectedUserId}
                      onValueChange={setSelectedUserId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar usuario..." />
                      </SelectTrigger>
                      <SelectContent>
                        {/* Show provider-role users that are not already assigned */}
                        {allUsers
                          .filter(u => {
                            if (u.role !== 'provider') return false;
                            // Check if already assigned to this provider
                            const isAssigned = selectedProvider?.assigned_users?.some(au => au.id === u.id);
                            return !isAssigned;
                          })
                          .map(u => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.name} ({u.email})
                            </SelectItem>
                          ))
                        }
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">
                      Solo se muestran usuarios con rol "Proveedor" no asignados a este proveedor
                    </p>
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setAssignUserDialogOpen(false)}>
                      Cerrar
                    </Button>
                    <Button 
                      className="btn-primary" 
                      disabled={!selectedUserId || submitting}
                      onClick={handleAssignUser}
                    >
                      {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Agregar Usuario
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <PriceFixCard />
            <SeoIntroRegenCard />
            <ImageBackfillCard />
            <VenueBackfillCard />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Site Logo */}
              <Card className="rounded-2xl border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Image className="w-5 h-5" />
                    Logo del Sitio
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex flex-col items-center gap-4">
                    {/* Current Logo Preview */}
                    <div className="w-full p-6 bg-gray-50 rounded-xl flex items-center justify-center min-h-[120px]">
                      {logoUrl ? (
                        <img 
                          src={getFullLogoUrl()} 
                          alt="Logo actual" 
                          className="max-h-24 max-w-full object-contain"
                        />
                      ) : (
                        <p className="text-gray-400 text-sm">Sin logo personalizado</p>
                      )}
                    </div>

                    <p className="text-sm text-gray-500 text-center">
                      {logoUrl ? 'Logo personalizado activo' : 'Usando logo por defecto'}
                    </p>

                    {/* Upload Controls */}
                    <div className="flex gap-3 w-full">
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                        onChange={handleLogoUpload}
                        className="hidden"
                        id="logo-upload"
                      />
                      <Button
                        className="flex-1 btn-primary"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                      >
                        {uploadingLogo ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        {uploadingLogo ? 'Subiendo...' : 'Subir Logo'}
                      </Button>
                      {logoUrl && (
                        <Button
                          variant="outline"
                          className="text-red-500 hover:bg-red-50"
                          onClick={handleDeleteLogo}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Eliminar
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Instructions */}
                  <div className="bg-blue-50 rounded-xl p-4">
                    <h4 className="font-medium text-blue-800 mb-2">Recomendaciones</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Formato recomendado: PNG con fondo transparente</li>
                      <li>• Tamaño máximo: 2MB</li>
                      <li>• Dimensiones sugeridas: 400x100 píxeles</li>
                      <li>• El logo aparecerá en la barra de navegación de todo el sitio</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* Dark Logo */}
              <Card className="rounded-2xl border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Moon className="w-5 h-5" />
                    Logo Modo Noche
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-full p-6 bg-gray-900 rounded-xl flex items-center justify-center min-h-[120px]">
                      {darkLogoUrl ? (
                        <img 
                          src={getFullDarkLogoUrl()} 
                          alt="Logo modo noche" 
                          className="max-h-24 max-w-full object-contain"
                        />
                      ) : (
                        <p className="text-gray-500 text-sm">Sin logo para modo noche</p>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 text-center">
                      {darkLogoUrl ? 'Logo modo noche activo' : 'Se usará el logo normal en modo noche'}
                    </p>
                    <div className="flex gap-3 w-full">
                      <input
                        ref={darkLogoInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                        onChange={handleDarkLogoUpload}
                        className="hidden"
                      />
                      <Button
                        className="flex-1 btn-primary"
                        onClick={() => darkLogoInputRef.current?.click()}
                        disabled={uploadingDarkLogo}
                      >
                        {uploadingDarkLogo ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        {uploadingDarkLogo ? 'Subiendo...' : 'Subir Logo Noche'}
                      </Button>
                      {darkLogoUrl && (
                        <Button
                          variant="outline"
                          className="text-red-500 hover:bg-red-50"
                          onClick={handleDeleteDarkLogo}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Eliminar
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-4">
                    <h4 className="font-medium text-blue-800 mb-2">Recomendaciones</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Logo con colores claros para fondo oscuro</li>
                      <li>• Formato: PNG con fondo transparente</li>
                      <li>• Tamaño máximo: 2MB</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* Site Info */}
              <Card className="rounded-2xl border-0 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Información del Sitio
                  </CardTitle>
                  {!editingSiteInfo ? (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="rounded-full"
                      onClick={() => setEditingSiteInfo(true)}
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Editar
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="rounded-full"
                        onClick={() => setEditingSiteInfo(false)}
                      >
                        Cancelar
                      </Button>
                      <Button 
                        size="sm" 
                        className="btn-primary rounded-full"
                        onClick={handleSaveSiteInfo}
                        disabled={savingSiteInfo}
                      >
                        {savingSiteInfo && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                        Guardar
                      </Button>
                    </div>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-sm text-gray-600 mb-2">Nombre de la Plataforma</p>
                    {editingSiteInfo ? (
                      <Input
                        value={siteInfo.platform_name}
                        onChange={(e) => setSiteInfo({ ...siteInfo, platform_name: e.target.value })}
                        placeholder="Nombre de la plataforma"
                      />
                    ) : (
                      <p className="font-semibold text-[#064E3B]">{siteInfo.platform_name}</p>
                    )}
                  </div>
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-sm text-gray-600 mb-2">Versión</p>
                    {editingSiteInfo ? (
                      <Input
                        value={siteInfo.version}
                        onChange={(e) => setSiteInfo({ ...siteInfo, version: e.target.value })}
                        placeholder="1.0.0"
                      />
                    ) : (
                      <p className="font-semibold text-[#064E3B]">{siteInfo.version}</p>
                    )}
                  </div>
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-sm text-gray-600 mb-2">Estado</p>
                    <Badge className="bg-green-100 text-green-700 border-0">Activo</Badge>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-sm text-gray-600 mb-2">Estadísticas</p>
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      <div>
                        <p className="text-2xl font-bold text-[#064E3B]">{stats?.total_users || 0}</p>
                        <p className="text-xs text-gray-500">Usuarios</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-[#064E3B]">{stats?.active_events || 0}</p>
                        <p className="text-xs text-gray-500">Eventos Activos</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Favicon */}
              <Card className="rounded-2xl border-0 shadow-sm md:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="w-5 h-5" />
                    Favicon del Sitio
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col md:flex-row gap-6">
                    {/* Favicon Preview */}
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-32 h-32 bg-gray-50 rounded-xl flex items-center justify-center border-2 border-dashed border-gray-200">
                        {faviconUrl ? (
                          <img 
                            src={faviconUrl.startsWith('/') ? `${API_URL}${faviconUrl}` : faviconUrl} 
                            alt="Favicon actual" 
                            className="w-16 h-16 object-contain"
                          />
                        ) : (
                          <div className="text-center">
                            <Globe className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                            <p className="text-xs text-gray-400">Sin favicon</p>
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 text-center">
                        {faviconUrl ? 'Favicon personalizado activo' : 'Usando favicon por defecto'}
                      </p>
                    </div>

                    {/* Upload Controls & Instructions */}
                    <div className="flex-1 space-y-4">
                      <div className="flex gap-3">
                        <input
                          ref={faviconInputRef}
                          type="file"
                          accept="image/png"
                          onChange={handleFaviconUpload}
                          className="hidden"
                          id="favicon-upload"
                        />
                        <Button
                          className="btn-primary"
                          onClick={() => faviconInputRef.current?.click()}
                          disabled={uploadingFavicon}
                        >
                          {uploadingFavicon ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          {uploadingFavicon ? 'Subiendo...' : 'Subir Favicon'}
                        </Button>
                        {faviconUrl && (
                          <Button
                            variant="outline"
                            className="text-red-500 hover:bg-red-50"
                            onClick={handleDeleteFavicon}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Eliminar
                          </Button>
                        )}
                      </div>
                      <div className="bg-blue-50 rounded-xl p-4">
                        <h4 className="font-medium text-blue-800 mb-2">Recomendaciones para el Favicon</h4>
                        <ul className="text-sm text-blue-700 space-y-1">
                          <li>• Formato: Solo PNG</li>
                          <li>• Tamaño máximo: 500KB</li>
                          <li>• Dimensiones recomendadas: 32x32 o 64x64 píxeles</li>
                          <li>• El favicon aparece en la pestaña del navegador</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Hero Images */}
              <Card className="rounded-2xl border-0 shadow-sm md:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Camera className="w-5 h-5" />
                    Fotos de Portada
                  </CardTitle>
                  <CardDescription>Las 4 imágenes que aparecen en la sección principal del sitio</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[0, 1, 2, 3].map((index) => {
                      const heroLabels = ['Conciertos', 'Turismo Aventura', 'Emprendimiento', 'Gastronomía'];
                      const img = heroImages[index];
                      const imgUrl = img?.url?.startsWith('/') ? `${API_URL}${img.url}` : (img?.url || '');
                      return (
                        <div key={index} className="space-y-2">
                          <p className="text-xs font-medium text-gray-500 text-center">{heroLabels[index]}</p>
                          <div className="relative aspect-[3/4] rounded-xl overflow-hidden border-2 border-dashed border-gray-200 bg-gray-50 group">
                            {imgUrl ? (
                              <img src={imgUrl} alt={heroLabels[index]} className="w-full h-full object-cover" />
                            ) : (
                              <div className="flex items-center justify-center h-full">
                                <Camera className="w-8 h-8 text-gray-300" />
                              </div>
                            )}
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                              <input
                                ref={heroInputRefs[index]}
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                onChange={(e) => handleHeroImageUpload(e, index)}
                                className="hidden"
                              />
                              <Button
                                size="sm"
                                className="bg-white text-gray-800 hover:bg-gray-100 rounded-full text-xs"
                                onClick={() => heroInputRefs[index]?.current?.click()}
                                disabled={uploadingHeroIndex === index}
                              >
                                {uploadingHeroIndex === index ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <Upload className="w-3 h-3 mr-1" />
                                )}
                                Cambiar
                              </Button>
                              {img?.url?.startsWith('/') && (
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  className="rounded-full text-xs"
                                  onClick={() => handleDeleteHeroImage(index)}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="bg-blue-50 rounded-xl p-4 mt-4">
                    <h4 className="font-medium text-blue-800 mb-2">Recomendaciones</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Formato: JPEG, PNG o WebP</li>
                      <li>• Tamaño máximo: 2MB por imagen</li>
                      <li>• Usa imágenes llamativas y de alta calidad</li>
                      <li>• Pasa el mouse sobre cada imagen para cambiarla</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* Social Proof (Trust Bar) */}
              <Card className="rounded-2xl border-0 shadow-sm md:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Prueba Social del Landing
                  </CardTitle>
                  <CardDescription>
                    Las 4 fotos de personas y el número que se muestra en la frase "Únete a más de X personas..."
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {/* Count editor */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Número de personas
                      </label>
                      <div className="flex gap-2 items-center max-w-md">
                        <Input
                          type="number"
                          min={0}
                          value={socialProofCountDraft}
                          onChange={(e) => setSocialProofCountDraft(e.target.value)}
                          placeholder="2500"
                          data-testid="social-proof-count-input"
                        />
                        <Button
                          onClick={handleSaveSocialProofCount}
                          disabled={savingSocialProofCount}
                          className="bg-[#00CC66] hover:bg-[#00b85b] text-white rounded-full"
                          data-testid="social-proof-count-save"
                        >
                          {savingSocialProofCount ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Se mostrará formateado: <span className="font-semibold">{Number(socialProofCountDraft || 0).toLocaleString('es-CL')} personas</span>
                      </p>
                    </div>

                    {/* Photos */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Fotos de personas (4)
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[0, 1, 2, 3].map((index) => {
                          const url = socialProof.photos?.[index] || '';
                          const fullUrl = url && url.startsWith('/') ? `${API_URL}${url}` : url;
                          const isUploadedAsset = url && url.startsWith('/');
                          return (
                            <div key={index} className="space-y-2">
                              <p className="text-xs font-medium text-gray-500 text-center">Foto {index + 1}</p>
                              <div className="relative aspect-square rounded-full overflow-hidden border-2 border-dashed border-gray-200 bg-gray-50 group">
                                {fullUrl ? (
                                  <img
                                    src={fullUrl}
                                    alt={`Foto ${index + 1}`}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="flex items-center justify-center h-full">
                                    <Camera className="w-8 h-8 text-gray-300" />
                                  </div>
                                )}
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 rounded-full">
                                  <input
                                    ref={socialProofInputRefs[index]}
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    onChange={(e) => handleSocialProofPhotoUpload(e, index)}
                                    className="hidden"
                                  />
                                  <Button
                                    size="sm"
                                    className="bg-white text-gray-800 hover:bg-gray-100 rounded-full text-xs"
                                    onClick={() => socialProofInputRefs[index]?.current?.click()}
                                    disabled={uploadingSocialIndex === index}
                                    data-testid={`social-proof-upload-${index}`}
                                  >
                                    {uploadingSocialIndex === index ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <Upload className="w-3 h-3 mr-1" />
                                    )}
                                    Cambiar
                                  </Button>
                                  {isUploadedAsset && (
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      className="rounded-full text-xs"
                                      onClick={() => handleResetSocialProofPhoto(index)}
                                      data-testid={`social-proof-reset-${index}`}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="bg-blue-50 rounded-xl p-4">
                      <h4 className="font-medium text-blue-800 mb-2">Tips</h4>
                      <ul className="text-sm text-blue-700 space-y-1">
                        <li>• Usa fotos cuadradas y de buena calidad (idealmente 200x200 px o más)</li>
                        <li>• Pasa el mouse sobre cada foto para cambiarla o restaurarla</li>
                        <li>• Tamaño máximo: 2MB por foto</li>
                        <li>• El número se muestra con separador de miles ("2.500 personas")</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          <TabsContent value="plans">
            <div className="space-y-6">
              {/* Header with Add Button */}
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-[#064E3B]">Gestión de Planes</h2>
                  <p className="text-gray-500">Configura los planes de membresía y sus precios</p>
                </div>
                <Dialog open={planDialogOpen} onOpenChange={setPlanDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      className="btn-primary"
                      onClick={() => {
                        setEditingPlan(null);
                        resetPlanForm();
                      }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Nuevo Plan
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingPlan ? 'Editar Plan' : 'Crear Nuevo Plan'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSavePlan} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Clave del Plan *</Label>
                          <Input
                            value={newPlan.plan_key}
                            onChange={(e) => setNewPlan({ ...newPlan, plan_key: e.target.value.toLowerCase() })}
                            placeholder="ej: basic, pro, premium"
                            required
                            disabled={!!editingPlan}
                          />
                          <p className="text-xs text-gray-500">Identificador único (no se puede cambiar después)</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Nombre del Plan *</Label>
                          <Input
                            value={newPlan.name}
                            onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })}
                            placeholder="ej: Plan Básico"
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Descripción</Label>
                        <Textarea
                          value={newPlan.description}
                          onChange={(e) => setNewPlan({ ...newPlan, description: e.target.value })}
                          placeholder="Descripción del plan..."
                          rows={2}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Precio Mensual (CLP)</Label>
                          <Input
                            type="number"
                            value={newPlan.price_monthly}
                            onChange={(e) => setNewPlan({ ...newPlan, price_monthly: parseInt(e.target.value) || 0 })}
                            min="0"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Precio Anual (CLP)</Label>
                          <Input
                            type="number"
                            value={newPlan.price_yearly}
                            onChange={(e) => setNewPlan({ ...newPlan, price_yearly: parseInt(e.target.value) || 0 })}
                            min="0"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Máximo de Eventos</Label>
                          <Input
                            type="number"
                            value={newPlan.max_events}
                            onChange={(e) => setNewPlan({ ...newPlan, max_events: parseInt(e.target.value) || 0 })}
                            min="-1"
                          />
                          <p className="text-xs text-gray-500">-1 = ilimitado</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Eventos Destacados</Label>
                          <Input
                            type="number"
                            value={newPlan.max_featured_events}
                            onChange={(e) => setNewPlan({ ...newPlan, max_featured_events: parseInt(e.target.value) || 0 })}
                            min="-1"
                          />
                          <p className="text-xs text-gray-500">-1 = ilimitado</p>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Orden de Visualización</Label>
                        <Input
                          type="number"
                          value={newPlan.display_order}
                          onChange={(e) => setNewPlan({ ...newPlan, display_order: parseInt(e.target.value) || 0 })}
                          min="0"
                        />
                      </div>

                      {/* Dynamic Features Section */}
                      <div className="space-y-3 p-4 bg-gray-50 rounded-xl">
                        <div className="flex justify-between items-center">
                          <Label className="text-base font-semibold">Características del Plan</Label>
                        </div>
                        
                        {/* List of features */}
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {newPlan.features.map((feature, index) => (
                            <div key={feature.id} className="flex items-center gap-2 p-2 bg-white rounded-lg border">
                              <div className="flex flex-col">
                                <button
                                  type="button"
                                  disabled={index === 0}
                                  onClick={() => handleMoveFeature(index, -1)}
                                  className="text-gray-400 hover:text-gray-700 disabled:opacity-20 disabled:cursor-not-allowed p-0 leading-none"
                                >
                                  <ChevronUp className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  disabled={index === newPlan.features.length - 1}
                                  onClick={() => handleMoveFeature(index, 1)}
                                  className="text-gray-400 hover:text-gray-700 disabled:opacity-20 disabled:cursor-not-allowed p-0 leading-none"
                                >
                                  <ChevronDown className="w-4 h-4" />
                                </button>
                              </div>
                              <span className="text-xs text-gray-400 w-5 text-center">{index + 1}</span>
                              <input
                                type="checkbox"
                                checked={feature.is_included}
                                onChange={(e) => handleUpdateFeature(feature.id, { is_included: e.target.checked })}
                                className="w-4 h-4 rounded border-gray-300"
                              />
                              <Input
                                value={feature.name}
                                onChange={(e) => handleUpdateFeature(feature.id, { name: e.target.value })}
                                className="flex-1 h-8"
                                placeholder="Nombre de la característica"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-600 h-8 w-8 p-0"
                                onClick={() => handleRemoveFeature(feature.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                        
                        {/* Add new feature */}
                        <div className="flex items-center gap-2 pt-2 border-t">
                          <input
                            type="checkbox"
                            checked={newFeature.is_included}
                            onChange={(e) => setNewFeature({ ...newFeature, is_included: e.target.checked })}
                            className="w-4 h-4 rounded border-gray-300"
                          />
                          <Input
                            value={newFeature.name}
                            onChange={(e) => setNewFeature({ ...newFeature, name: e.target.value })}
                            placeholder="Nueva característica..."
                            className="flex-1 h-8"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddFeature();
                              }
                            }}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleAddFeature}
                            className="h-8"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-xs text-gray-500">
                          Marca la casilla si la característica está incluida en el plan. Desmárcala si no está incluida.
                        </p>
                      </div>

                      {/* Plan Active Toggle */}
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={newPlan.is_active}
                            onChange={(e) => setNewPlan({ ...newPlan, is_active: e.target.checked })}
                            className="w-4 h-4 rounded border-gray-300"
                          />
                          <span>Plan Activo (visible para usuarios)</span>
                        </label>
                      </div>

                      <div className="flex justify-end gap-2 pt-4">
                        <Button type="button" variant="outline" onClick={() => setPlanDialogOpen(false)}>
                          Cancelar
                        </Button>
                        <Button type="submit" className="btn-primary" disabled={submitting}>
                          {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                          {editingPlan ? 'Guardar Cambios' : 'Crear Plan'}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Plans Grid */}
              {loadingPlans ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-80 rounded-2xl" />)}
                </div>
              ) : plans.length === 0 ? (
                <Card className="rounded-2xl border-0 shadow-sm">
                  <CardContent className="py-12 text-center">
                    <CreditCard className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">No hay planes configurados</p>
                    <p className="text-sm text-gray-400">Crea tu primer plan para que los proveedores puedan suscribirse</p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Billing Toggle Preview */}
                  <div className="flex items-center justify-center gap-5">
                    <span className={`text-sm font-medium transition-colors ${adminBillingPreview === 'monthly' ? 'text-[#064E3B]' : 'text-gray-400'}`}>
                      Mensual
                    </span>
                    <button
                      onClick={() => setAdminBillingPreview(adminBillingPreview === 'monthly' ? 'yearly' : 'monthly')}
                      className={`relative w-11 h-6 rounded-full transition-colors duration-200 flex-shrink-0 ${
                        adminBillingPreview === 'yearly' ? 'bg-[#00CC66]' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                        adminBillingPreview === 'yearly' ? 'translate-x-[22px]' : 'translate-x-0.5'
                      }`} />
                    </button>
                    <span className={`text-sm font-medium transition-colors ${adminBillingPreview === 'yearly' ? 'text-[#064E3B]' : 'text-gray-400'}`}>
                      Anual
                    </span>
                    {adminBillingPreview === 'yearly' && (
                      <Badge className="bg-green-100 text-green-700 border-0 text-xs">
                        Vista anual
                      </Badge>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                    {plans.map((plan) => {
                      const planColors = {
                        basic: { bg: 'bg-slate-50', border: 'border-slate-200', accent: 'text-slate-600' },
                        pro: { bg: 'bg-blue-50', border: 'border-blue-300', accent: 'text-blue-600' },
                        premium: { bg: 'bg-amber-50', border: 'border-amber-300', accent: 'text-amber-600' },
                      };
                      const colors = planColors[plan.plan_key] || { bg: 'bg-gray-50', border: 'border-gray-200', accent: 'text-gray-600' };
                      const price = adminBillingPreview === 'yearly' ? plan.price_yearly : plan.price_monthly;
                      const savings = plan.price_monthly > 0 ? (plan.price_monthly * 12) - plan.price_yearly : 0;
                      const eventsLabel = plan.max_events === -1 ? 'Publicación ilimitada de eventos' : `Hasta ${plan.max_events} eventos`;
                      const featuredLabel = plan.max_featured_events === -1 ? 'Eventos destacados ilimitados' : plan.max_featured_events === 0 ? null : `${plan.max_featured_events} eventos destacados`;

                      return (
                        <Card
                          key={plan.id}
                          className={`rounded-2xl transition-all duration-200 ${!plan.is_active ? 'opacity-60' : ''} border-2 ${colors.border} ${colors.bg} shadow-sm`}
                        >
                          <CardHeader className="pb-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {plan.plan_key === 'basic' && <Zap className={`w-5 h-5 ${colors.accent}`} />}
                                {plan.plan_key === 'pro' && <Star className={`w-5 h-5 ${colors.accent}`} />}
                                {plan.plan_key === 'premium' && <Crown className={`w-5 h-5 ${colors.accent}`} />}
                                {!['basic', 'pro', 'premium'].includes(plan.plan_key) && <CreditCard className={`w-5 h-5 ${colors.accent}`} />}
                                <CardTitle className="text-lg text-[#064E3B]">{plan.name}</CardTitle>
                              </div>
                              <div className="flex items-center gap-1">
                                {!plan.is_active && (
                                  <Badge className="bg-gray-200 text-gray-600 border-0 text-xs mr-1">Inactivo</Badge>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={() => openEditPlanDialog(plan)}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-red-500 hover:text-red-600"
                                  onClick={() => handleDeletePlan(plan.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                            <CardDescription className="text-sm">{plan.description}</CardDescription>
                            <p className="text-xs text-gray-400">Clave: {plan.plan_key}</p>
                          </CardHeader>
                          <CardContent>
                            {/* Price */}
                            <div className="mb-5">
                              <div className="flex items-baseline gap-1">
                                <span className="text-3xl font-bold text-[#064E3B]">
                                  {price === 0 ? 'Gratis' : formatCurrency(price)}
                                </span>
                                {price > 0 && (
                                  <span className="text-gray-500 text-sm">
                                    /{adminBillingPreview === 'yearly' ? 'año' : 'mes'}
                                  </span>
                                )}
                              </div>
                              {adminBillingPreview === 'yearly' && savings > 0 && (
                                <p className="text-green-600 text-xs font-medium mt-1">
                                  Ahorro: {formatCurrency(savings)} al año
                                </p>
                              )}
                            </div>

                            {/* Limits */}
                            <div className="space-y-2 mb-3 pb-3 border-b border-gray-200/60">
                              <div className="flex items-center gap-2 text-sm">
                                <Calendar className="w-4 h-4 text-[#00CC66] flex-shrink-0" />
                                <span className="text-gray-700">{eventsLabel}</span>
                              </div>
                              {featuredLabel && (
                                <div className="flex items-center gap-2 text-sm">
                                  <Star className="w-4 h-4 text-[#00CC66] flex-shrink-0" />
                                  <span className="text-gray-700">{featuredLabel}</span>
                                </div>
                              )}
                            </div>

                            {/* Features */}
                            <ul className="space-y-2 mb-4">
                              {plan.features && plan.features.map((feature) => (
                                <li key={feature.id} className="flex items-start gap-2 text-sm">
                                  {feature.is_included ? (
                                    <CheckCircle className="w-4 h-4 text-[#00CC66] mt-0.5 flex-shrink-0" />
                                  ) : (
                                    <XCircle className="w-4 h-4 text-gray-300 mt-0.5 flex-shrink-0" />
                                  )}
                                  <span className={feature.is_included ? 'text-gray-700' : 'text-gray-400'}>{feature.name}</span>
                                </li>
                              ))}
                              {(!plan.features || plan.features.length === 0) && (
                                <li className="text-xs text-gray-400 italic">Sin características definidas</li>
                              )}
                            </ul>

                            {/* Subscribers */}
                            <div className="pt-3 border-t border-gray-200/60">
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-500">Suscriptores</span>
                                <Badge className="bg-green-100 text-green-700 border-0">
                                  {plan.subscribers_count}
                                </Badge>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions">
            <div className="space-y-6">
              {/* Stats Cards */}
              {subscriptionStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="rounded-2xl border-0 shadow-sm">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                          <Bell className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-[#064E3B]">{subscriptionStats.total}</p>
                          <p className="text-sm text-gray-500">Total Suscripciones</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="rounded-2xl border-0 shadow-sm">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                          <CheckCircle className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-[#064E3B]">{subscriptionStats.active}</p>
                          <p className="text-sm text-gray-500">Activas</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="rounded-2xl border-0 shadow-sm">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                          <Tag className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-[#064E3B]">{subscriptionStats.by_category}</p>
                          <p className="text-sm text-gray-500">Por Categoría</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="rounded-2xl border-0 shadow-sm">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                          <Store className="w-6 h-6 text-orange-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-[#064E3B]">{subscriptionStats.by_provider}</p>
                          <p className="text-sm text-gray-500">Por Proveedor</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Subscriptions Table */}
              <Card className="rounded-2xl border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    Lista de Suscriptores
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingSubscriptions ? (
                    <div className="space-y-4">
                      {[1, 2, 3].map((i) => (
                        <Skeleton key={i} className="h-16 w-full" />
                      ))}
                    </div>
                  ) : subscriptions.length === 0 ? (
                    <div className="text-center py-12">
                      <Bell className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500">No hay suscriptores aún</p>
                      <p className="text-sm text-gray-400 mt-1">
                        Los usuarios pueden suscribirse desde la página de detalle de eventos
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-4 font-medium text-gray-600">Nombre</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-600">Email</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-600">Comuna</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-600">Tipo</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-600">Suscrito a</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-600">Estado</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-600">Fecha</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-600">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {subscriptions.map((sub) => (
                            <tr key={sub.id} className="border-b hover:bg-gray-50">
                              <td className="py-3 px-4 font-medium">{sub.name}</td>
                              <td className="py-3 px-4">
                                <a href={`mailto:${sub.email}`} className="text-blue-600 hover:underline">
                                  {sub.email}
                                </a>
                              </td>
                              <td className="py-3 px-4 text-gray-600">{sub.commune_name || '-'}</td>
                              <td className="py-3 px-4">
                                <Badge className={sub.subscription_type === 'category' 
                                  ? 'bg-purple-100 text-purple-700 border-0' 
                                  : 'bg-orange-100 text-orange-700 border-0'
                                }>
                                  {sub.subscription_type === 'category' ? 'Categoría' : 'Proveedor'}
                                </Badge>
                              </td>
                              <td className="py-3 px-4 text-gray-600">
                                {sub.category_name || sub.provider_name || '-'}
                              </td>
                              <td className="py-3 px-4">
                                <Badge 
                                  className={sub.is_active 
                                    ? 'bg-green-100 text-green-700 border-0 cursor-pointer' 
                                    : 'bg-gray-100 text-gray-500 border-0 cursor-pointer'
                                  }
                                  onClick={() => handleToggleSubscription(sub.id)}
                                >
                                  {sub.is_active ? 'Activo' : 'Inactivo'}
                                </Badge>
                              </td>
                              <td className="py-3 px-4 text-gray-600 text-sm">
                                {formatChile(sub.created_at, 'dd/MM/yyyy')}
                              </td>
                              <td className="py-3 px-4 text-right">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-500 hover:bg-red-50"
                                  onClick={() => handleDeleteSubscription(sub.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Info Card */}
              <Card className="rounded-2xl border-0 shadow-sm bg-blue-50">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-blue-800 mb-1">Sistema de Notificaciones</h4>
                      <p className="text-sm text-blue-700">
                        Los suscriptores activos recibirán notificaciones por email cuando se publiquen nuevos 
                        eventos de la categoría o proveedor al que se suscribieron. Para activar el envío 
                        automático de emails, se requiere integración con un servicio como SendGrid o similar.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Enganches Tab */}
          <TabsContent value="enganches">
            <EngancheTab token={localStorage.getItem('token')} />
          </TabsContent>

          {/* Platform Messages (Contact form inbox) */}
          <TabsContent value="messages">
            <Card className="rounded-2xl border-0 shadow-sm" data-testid="messages-tab-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-[#064E3B]" />
                    Mensajes de contacto
                  </CardTitle>
                  <CardDescription>
                    {unreadMessagesCount > 0
                      ? `${unreadMessagesCount} sin leer · ${platformMessages.length} en total`
                      : `${platformMessages.length} mensajes en total`}
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchPlatformMessages}
                  disabled={loadingMessages}
                  className="rounded-full"
                  data-testid="messages-refresh-btn"
                >
                  {loadingMessages ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Actualizar'}
                </Button>
              </CardHeader>
              <CardContent>
                {loadingMessages ? (
                  <div className="space-y-3">
                    <Skeleton className="h-24 w-full rounded-xl" />
                    <Skeleton className="h-24 w-full rounded-xl" />
                  </div>
                ) : platformMessages.length === 0 ? (
                  <div className="text-center py-16 text-gray-500" data-testid="messages-empty">
                    <Mail className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium">No hay mensajes aún</p>
                    <p className="text-sm mt-1">Los mensajes enviados desde <code>/contacto</code> aparecerán aquí.</p>
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="messages-list">
                    {platformMessages.map((m) => (
                      <div
                        key={m.id}
                        className={`rounded-xl border p-4 transition-colors ${
                          m.is_read ? 'bg-white border-gray-200' : 'bg-amber-50/60 border-amber-200'
                        }`}
                        data-testid={`message-row-${m.id}`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <span className="font-semibold text-[#064E3B]" data-testid={`message-name-${m.id}`}>
                                {m.name}
                              </span>
                              {!m.is_read && (
                                <Badge className="bg-amber-500 text-white text-[10px] uppercase tracking-wide rounded-full">
                                  Nuevo
                                </Badge>
                              )}
                              <Badge variant="outline" className="rounded-full text-[10px]">
                                {subjectLabel(m.subject)}
                              </Badge>
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500 mb-2">
                              <a
                                href={`mailto:${m.email}`}
                                className="hover:text-[#00CC66] inline-flex items-center gap-1"
                                data-testid={`message-email-${m.id}`}
                              >
                                <Mail className="w-3 h-3" /> {m.email}
                              </a>
                              {m.phone && (
                                <span className="inline-flex items-center gap-1">
                                  <Phone className="w-3 h-3" /> {m.phone}
                                </span>
                              )}
                              <span>{m.created_at ? formatChile(m.created_at, 'dd-MM-yyyy HH:mm') : ''}</span>
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                              {m.message}
                            </p>
                          </div>
                          <div className="flex sm:flex-col gap-2 shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleToggleMessageRead(m.id)}
                              className="rounded-full"
                              data-testid={`message-toggle-${m.id}`}
                            >
                              {m.is_read ? 'Marcar no leído' : 'Marcar leído'}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeletePlatformMessage(m.id)}
                              className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                              data-testid={`message-delete-${m.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* AI Events Tab */}
          <TabsContent value="ai-events">
            <AIEventsTab token={localStorage.getItem('token')} />
          </TabsContent>

          {/* Web Scraping Tab (restricted to cristian@plancity.cl) */}
          {(user?.email || '').toLowerCase() === 'cristian@plancity.cl' && (
            <TabsContent value="scraping">
              <ScrapingTab />
            </TabsContent>
          )}

          {/* Organizadores (restricted to cristian@plancity.cl) */}
          {(user?.email || '').toLowerCase() === 'cristian@plancity.cl' && (
            <TabsContent value="organizers">
              <OrganizersTab />
            </TabsContent>
          )}

          {/* Boost Vistas (restricted to cristian@plancity.cl) */}
          {(user?.email || '').toLowerCase() === 'cristian@plancity.cl' && (
            <TabsContent value="boost">
              <ViewsBoostTab />
            </TabsContent>
          )}

          {/* Duplicados (restricted to cristian@plancity.cl) */}
          {(user?.email || '').toLowerCase() === 'cristian@plancity.cl' && (
            <TabsContent value="duplicates">
              <DuplicatesTab />
            </TabsContent>
          )}

          {/* Venues Tab */}
          <TabsContent value="venues">
            <VenuesTab />
          </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Event views evolution chart modal */}
      <Dialog open={!!chartEvent} onOpenChange={(open) => { if (!open) { setChartEvent(null); setChartData(null); setHoverIdx(null); } }}>
        <DialogContent className="max-w-3xl" data-testid="event-chart-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              <span className="truncate">{chartEvent?.name || 'Evolución'}</span>
            </DialogTitle>
            <DialogDescription>
              {chartEvent?.commune_name && <span className="text-emerald-700 font-medium mr-2">📍 {chartEvent.commune_name}{chartEvent.region_name && chartEvent.region_name !== chartEvent.commune_name ? `, ${chartEvent.region_name}` : ''}</span>}
              Evolución diaria de visualizaciones y visitantes únicos.
            </DialogDescription>
          </DialogHeader>

          {/* Range switch: 3 / 7 / 30 days */}
          <div className="flex items-center gap-2 flex-wrap">
            {[3, 7, 30].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => { setChartDays(n); if (chartEvent) loadChartSeries(chartEvent.id, n); }}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${chartDays === n ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50'}`}
                data-testid={`chart-range-${n}`}
              >
                Últimos {n} días
              </button>
            ))}
            <div className="ml-auto text-xs text-gray-500">
              {chartData && (
                <span>
                  Período: <b className="text-[#064E3B]">{chartData.total_views.toLocaleString('es-CL')}</b> vistas · <b className="text-[#064E3B]">{chartData.total_unique.toLocaleString('es-CL')}</b> únicos
                  {chartData.event_total_views != null && (
                    <span className="ml-2 pl-2 border-l border-gray-200">
                      Total histórico: <b className="text-emerald-700">{chartData.event_total_views.toLocaleString('es-CL')}</b>
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>

          {/* Chart body */}
          <div className="mt-2 rounded-xl border bg-gradient-to-br from-emerald-50/50 to-white p-4">
            {chartLoading || !chartData ? (
              <div className="h-56 flex items-center justify-center text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…
              </div>
            ) : (() => {
              const series = chartData.series;
              const maxV = Math.max(1, ...series.map((p) => p.views), ...series.map((p) => p.unique));
              // Use round numbers on the Y axis so ticks look tidy.
              const niceMax = (() => {
                if (maxV <= 5) return 5;
                const pow = Math.pow(10, Math.floor(Math.log10(maxV)));
                const rel = maxV / pow;
                let m = 10;
                if (rel <= 1) m = 1;
                else if (rel <= 2) m = 2;
                else if (rel <= 5) m = 5;
                return m * pow;
              })();
              const w = 900, h = 320, padL = 52, padR = 16, padT = 16, padB = 52;
              const chartW = w - padL - padR, chartH = h - padT - padB;
              const xAt = (i) => padL + (series.length <= 1 ? chartW/2 : (chartW * i) / (series.length - 1));
              const yAt = (v) => padT + chartH - (v / niceMax) * chartH;
              const linePath = series.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i)} ${yAt(p.views)}`).join(' ');
              const uniquePath = series.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i)} ${yAt(p.unique)}`).join(' ');
              const areaPath = `${linePath} L ${xAt(series.length - 1)} ${padT + chartH} L ${xAt(0)} ${padT + chartH} Z`;
              // 5 y-axis ticks with rounded values
              const yTicks = [0, 0.25, 0.5, 0.75, 1].map((r) => ({
                y: padT + chartH * (1 - r),
                v: Math.round(niceMax * r),
              }));
              // Show every day when window is small (<=7); otherwise stride.
              const maxLabels = series.length <= 7 ? series.length : 8;
              const step = Math.max(1, Math.ceil(series.length / maxLabels));
              const fmtLabel = (iso) => {
                const parts = iso.split('-');
                return parts.length === 3 ? `${parts[2]}/${parts[1]}` : iso;
              };
              const fmtFull = (iso) => {
                const d = new Date(iso + 'T12:00:00');
                return d.toLocaleDateString('es-CL', { weekday: 'short', day: '2-digit', month: 'short' });
              };
              const hovered = hoverIdx != null && series[hoverIdx] ? series[hoverIdx] : null;
              // Build raw SVG markup for all dynamic elements (axes, ticks, labels, points).
              // We inject via dangerouslySetInnerHTML to bypass the visual-editor <span> wrapper
              // that breaks SVG rendering when JSX .map() is used directly inside <svg>.
              const gridAndTicks = yTicks.map((t) => `
                <line x1="${padL}" x2="${w - padR}" y1="${t.y}" y2="${t.y}" stroke="#e5e7eb" stroke-dasharray="4 4" stroke-width="1" vector-effect="non-scaling-stroke"/>
                <text x="${padL - 8}" y="${t.y}" text-anchor="end" dominant-baseline="middle" font-size="18" font-family="system-ui, -apple-system, sans-serif" font-weight="600" fill="#111827">${t.v}</text>
              `).join('');
              const xTicks = series.map((p, i) => {
                const isLast = i === series.length - 1;
                const isStride = i % step === 0;
                // Show if striding, OR if this is the last day AND it's not too close to the previous stride label.
                let show = isStride;
                if (!show && isLast) {
                  const prevStrideIdx = Math.floor(i / step) * step;
                  show = (i - prevStrideIdx) > Math.max(1, Math.floor(step / 2));
                }
                if (!show) return '';
                return `
                  <line x1="${xAt(i)}" x2="${xAt(i)}" y1="${padT + chartH}" y2="${padT + chartH + 5}" stroke="#374151" stroke-width="1" vector-effect="non-scaling-stroke"/>
                  <text x="${xAt(i)}" y="${padT + chartH + 24}" text-anchor="middle" font-size="17" font-family="system-ui, -apple-system, sans-serif" font-weight="600" fill="#111827">${fmtLabel(p.date)}</text>
                `;
              }).join('');
              const dataPoints = series.map((p, i) => {
                const r = hoverIdx === i ? 6 : 3.5;
                const strokeAttr = hoverIdx === i ? 'stroke="#fff" stroke-width="2"' : '';
                return `<circle cx="${xAt(i)}" cy="${yAt(p.views)}" r="${r}" fill="#059669" ${strokeAttr} vector-effect="non-scaling-stroke" pointer-events="none"/>`;
              }).join('');
              const hoverMarker = hoverIdx != null
                ? `<line x1="${xAt(hoverIdx)}" x2="${xAt(hoverIdx)}" y1="${padT}" y2="${padT + chartH}" stroke="#059669" stroke-width="1.5" stroke-dasharray="3 4" opacity="0.7" vector-effect="non-scaling-stroke"/>`
                : '';
              const dynamicSvg =
                gridAndTicks +
                xTicks +
                `<path d="${areaPath}" fill="url(#viewsGradient)"/>` +
                `<path d="${linePath}" fill="none" stroke="#059669" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round" vector-effect="non-scaling-stroke"/>` +
                `<path d="${uniquePath}" fill="none" stroke="#6366f1" stroke-width="1.8" stroke-dasharray="5 4" stroke-linejoin="round" vector-effect="non-scaling-stroke"/>` +
                hoverMarker +
                dataPoints;
              // Given a client-space mouse X, find the nearest data-point index.
              const handleSvgMove = (evt) => {
                const svg = evt.currentTarget;
                const pt = svg.createSVGPoint();
                pt.x = evt.clientX;
                pt.y = evt.clientY;
                const inv = svg.getScreenCTM()?.inverse();
                if (!inv) return;
                const local = pt.matrixTransform(inv);
                if (local.x < padL || local.x > w - padR) return;
                // Find closest index by x
                let best = 0, bestDist = Infinity;
                for (let i = 0; i < series.length; i += 1) {
                  const d = Math.abs(xAt(i) - local.x);
                  if (d < bestDist) { bestDist = d; best = i; }
                }
                if (best !== hoverIdx) setHoverIdx(best);
              };
              return (
                <div className="relative w-full" data-testid="event-chart-wrapper" style={{ aspectRatio: `${w} / ${h}` }}>
                  <svg
                    viewBox={`0 0 ${w} ${h}`}
                    preserveAspectRatio="xMidYMid meet"
                    className="absolute inset-0 w-full h-full"
                    data-testid="event-chart-svg"
                    onMouseMove={handleSvgMove}
                    onMouseLeave={() => setHoverIdx(null)}
                    style={{ overflow: 'visible', cursor: 'crosshair' }}
                  >
                    <defs>
                      <linearGradient id="viewsGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity="0.35" />
                        <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    {/* Static axis lines (rendered directly because static children are unaffected by VE wrapper) */}
                    <line x1={padL} x2={padL} y1={padT} y2={padT + chartH} stroke="#374151" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
                    <line x1={padL} x2={w - padR} y1={padT + chartH} y2={padT + chartH} stroke="#374151" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
                    {/* Dynamic content injected as raw SVG to bypass VE <span> wrapping */}
                    <g dangerouslySetInnerHTML={{ __html: dynamicSvg }} />
                    {/* Axis titles (static text renders normally) */}
                    <text
                      x={16}
                      y={padT + chartH / 2}
                      transform={`rotate(-90 16 ${padT + chartH / 2})`}
                      textAnchor="middle"
                      fontSize="15"
                      fontFamily="system-ui, -apple-system, sans-serif"
                      fontWeight="700"
                      fill="#374151"
                    >
                      Visitas
                    </text>
                    <text
                      x={padL + chartW / 2}
                      y={h - 6}
                      textAnchor="middle"
                      fontSize="15"
                      fontFamily="system-ui, -apple-system, sans-serif"
                      fontWeight="700"
                      fill="#374151"
                    >
                      Días
                    </text>
                  </svg>
                  {/* Hover tooltip (HTML overlay) */}
                  {hovered && (
                    <div
                      className="pointer-events-none absolute bg-[#064E3B] text-white text-xs rounded-lg shadow-xl px-3 py-2 -translate-x-1/2 z-10"
                      style={{
                        left: `${(xAt(hoverIdx) / w) * 100}%`,
                        top: `${(yAt(hovered.views) / h) * 100}%`,
                        transform: 'translate(-50%, calc(-100% - 10px))',
                        whiteSpace: 'nowrap',
                      }}
                      data-testid="chart-tooltip"
                    >
                      <div className="font-semibold">{fmtFull(hovered.date)}</div>
                      <div className="text-emerald-300 font-bold">{hovered.views.toLocaleString('es-CL')} vistas</div>
                      <div className="text-indigo-300">{hovered.unique.toLocaleString('es-CL')} únicos</div>
                    </div>
                  )}
                </div>
              );
            })()}
            <div className="flex items-center gap-4 mt-2 text-[11px] text-gray-600">
              <span className="inline-flex items-center gap-1"><span className="w-3 h-0.5 bg-emerald-600 rounded-full" /> Vistas</span>
              <span className="inline-flex items-center gap-1"><span className="w-3 h-0.5 bg-indigo-500 rounded-full" style={{ borderTop: '1px dashed #6366f1' }} /> Únicos</span>
              {chartData?.backfilled > 0 && (
                <span className="ml-auto text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                  Se reconciliaron {chartData.backfilled.toLocaleString('es-CL')} visitas históricas
                </span>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Confirmar Eliminación
            </AlertDialogTitle>            <AlertDialogDescription className="text-left">
              {deleteTarget.type === 'region' ? (
                <>
                  ¿Estás seguro de eliminar la región <strong>"{deleteTarget.name}"</strong> y todas sus comunas asociadas?
                  <br /><br />
                  <span className="text-red-500 font-medium">Esta acción no se puede deshacer.</span>
                </>
              ) : (
                <>
                  ¿Estás seguro de eliminar la comuna <strong>"{deleteTarget.name}"</strong>?
                  <br /><br />
                  <span className="text-red-500 font-medium">Esta acción no se puede deshacer.</span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Eliminando...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Generic Confirmation Dialog */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => !open && closeConfirmDialog()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmDialog.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDialog.onConfirm}
              className={confirmDialog.variant === 'destructive' ? 'bg-red-500 hover:bg-red-600 text-white' : 'btn-primary'}
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Footer />
    </div>
  );
};


// Small inline form for the Telegram wiring modal. Kept out of the parent
// component so its local input state doesn't force AdminDashboard re-renders.
function TelegramEditorForm({ user, apiUrl, onClose, onSaved }) {
  const [chatId, setChatId] = React.useState(user.telegram_chat_id || '');
  const [enabled, setEnabled] = React.useState(!!user.telegram_notifications_enabled);
  const [saving, setSaving] = React.useState(false);
  const [testing, setTesting] = React.useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${apiUrl}/api/admin/users/${user.id}/telegram`, {
        telegram_chat_id: chatId,
        telegram_notifications_enabled: enabled,
      });
      toast.success('Telegram actualizado');
      onSaved(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!chatId.trim()) {
      toast.error('Guarda el chat_id primero');
      return;
    }
    setTesting(true);
    try {
      await axios.post(`${apiUrl}/api/admin/users/${user.id}/telegram/test`);
      toast.success('Mensaje de prueba enviado — revisa Telegram');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fallo al enviar prueba');
    } finally { setTesting(false); }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <label className="text-sm font-medium">Chat ID</label>
        <input
          type="text"
          value={chatId}
          onChange={(e) => setChatId(e.target.value)}
          placeholder="Ej: 235196800"
          className="w-full border rounded-md px-3 py-2 text-sm font-mono"
          data-testid="telegram-chat-id-input"
        />
        <p className="text-xs text-gray-500">
          Obtén tu ID hablándole al bot y usando <code>/start</code>, o desde <code>@userinfobot</code>.
        </p>
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="w-4 h-4"
          data-testid="telegram-enabled-toggle"
        />
        <span className="text-sm">Notificaciones activas</span>
      </label>
      <div className="flex justify-between items-center pt-2">
        <button
          type="button"
          onClick={sendTest}
          disabled={testing || !user.telegram_chat_id}
          className="text-sm px-3 py-1.5 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
          data-testid="telegram-test-btn"
          title={!user.telegram_chat_id ? 'Guarda un chat_id primero' : 'Envía un mensaje de prueba'}
        >
          {testing ? 'Enviando…' : 'Enviar prueba'}
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="text-sm px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            data-testid="telegram-save-btn"
          >
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

